<template>
  <div class="home-container">
    <el-container>
      <el-header class="header">
        <div class="header-left">
          <h2>智拓星管理系统</h2>
        </div>
        <div class="header-right">
          <!-- 消息中心图标：根据菜单权限显示 -->
          <div 
            v-if="canShowMessageCenter"
            class="message-icon-wrapper" 
            @click="handleMessageCenterClick"
            :class="{ 'has-unread': unreadMessageCount > 0 }"
          >
            <el-badge v-if="unreadMessageCount > 0" :value="unreadMessageCount" :max="99" class="message-badge">
              <el-icon :size="28" class="message-icon">
                <Message />
              </el-icon>
            </el-badge>
            <el-icon v-else :size="28" class="message-icon">
              <Message />
            </el-icon>
          </div>
          <span class="user-info">{{ userInfo.realName || userInfo.username }}</span>
          <el-button type="text" @click="openChangePasswordDialog">修改密码</el-button>
          <el-button type="text" @click="handleLogout">退出</el-button>
        </div>
      </el-header>

      <!-- 修改密码对话框 -->
      <el-dialog
        v-model="changePasswordDialogVisible"
        title="修改密码"
        width="420px"
        :close-on-click-modal="false"
      >
        <div class="change-password-tip">
          为了您的账号安全，请定期修改密码
        </div>
        <el-form
          ref="changePasswordFormRef"
          :model="changePasswordForm"
          :rules="changePasswordRules"
          label-width="80px"
          class="change-password-form"
        >
          <el-form-item label="原密码" prop="oldPassword">
            <el-input
              v-model="changePasswordForm.oldPassword"
              type="password"
              show-password
              placeholder="请输入原密码"
            />
          </el-form-item>
          <el-form-item label="新密码" prop="newPassword">
            <el-input
              v-model="changePasswordForm.newPassword"
              type="password"
              show-password
              placeholder="请输入新密码"
            />
          </el-form-item>
          <el-form-item label="确认密码" prop="confirmPassword">
            <el-input
              v-model="changePasswordForm.confirmPassword"
              type="password"
              show-password
              placeholder="请再次输入新密码"
            />
          </el-form-item>
        </el-form>
        <template #footer>
          <span class="dialog-footer">
            <el-button @click="changePasswordDialogVisible = false">取 消</el-button>
            <el-button type="primary" :loading="changePasswordLoading" @click="handleChangePassword">
              确认修改
            </el-button>
          </span>
        </template>
        <div class="change-password-notice">
          <p>· 密码长度应在6-20位之间</p>
          <p>· 建议使用字母、数字和特殊字符组合</p>
          <p>· 定期修改密码可提高账号安全性</p>
        </div>
      </el-dialog>
      
      <el-container class="main-layout">
        <!-- 左侧菜单栏 -->
        <el-aside class="sidebar" :width="isCollapse ? '64px' : '220px'">
          <!-- 收起/展开按钮 - 放在菜单右侧中间位置 -->
          <div class="sidebar-toggle-wrapper">
            <el-tooltip :content="isCollapse ? '展开菜单' : '收起菜单'" placement="right" :disabled="isCollapse">
              <div class="sidebar-toggle" @click="toggleSidebar">
                <el-icon :size="18" :class="{ 'rotate-icon': isCollapse }">
                  <ArrowLeft v-if="!isCollapse" />
                  <ArrowRight v-else />
                </el-icon>
              </div>
            </el-tooltip>
          </div>
          
          <el-menu
            ref="menuRef"
            :default-active="activeMenu"
            :active-text-color="'#409eff'"
            class="sidebar-menu"
            mode="vertical"
            :router="true"
            :collapse="isCollapse"
            @select="handleMenuSelect"
            @open="handleSubMenuOpen"
          >
            <el-menu-item index="/home">
              <el-icon><HomeFilled /></el-icon>
              <span>首页</span>
            </el-menu-item>
            
            <!-- 动态菜单 -->
            <template v-for="menu in processedMenuList" :key="menu.id">
              <el-menu-item 
                v-if="menu.menuType === 'MENU'"
                :index="getMenuIndex(menu)"
                class="menu-item-with-badge"
              >
                <el-icon v-if="getMenuIconName(menu)">
                  <component :is="renderMenuIcon(getMenuIconName(menu))" />
                </el-icon>
                <span class="menu-text-wrapper">
                  {{ menu.menuName }}
                  <el-badge 
                    v-if="isApprovalMenu(menu) && approvalPendingCount > 0" 
                    :value="approvalPendingCount" 
                    class="menu-badge-inline"
                  />
                </span>
              </el-menu-item>
              
              <el-sub-menu 
                v-else-if="menu.menuType === 'DIRECTORY'"
                :index="`dir-${menu.id}`"
              >
                <template #title>
                  <el-icon v-if="getMenuIconName(menu)">
                    <component :is="renderMenuIcon(getMenuIconName(menu))" />
                  </el-icon>
                  <span>{{ menu.menuName }}</span>
                </template>
                <template v-for="child in menu.children" :key="child.id">
                  <!-- MENU 类型且有子菜单，渲染为 sub-menu，但 title 可点击跳转 -->
                  <el-sub-menu 
                    v-if="child.menuType === 'MENU' && child.children && child.children.length > 0"
                    :index="`menu-${child.id}`"
                  >
                    <template #title>
                      <div 
                        class="sub-menu-title-clickable" 
                        @click.stop="handleMenuClick(getMenuIndex(child))"
                      >
                        <el-icon v-if="getMenuIconName(child)">
                          <component :is="renderMenuIcon(getMenuIconName(child))" />
                        </el-icon>
                        <span>{{ child.menuName }}</span>
                      </div>
                    </template>
                    <template v-for="grandchild in child.children" :key="grandchild.id">
                      <el-menu-item 
                        v-if="grandchild.menuType === 'MENU' && grandchild.status === 1 && grandchild.visible === 1"
                        :index="getMenuIndex(grandchild)"
                        class="menu-item-with-badge"
                      >
                        <el-icon v-if="getMenuIconName(grandchild)">
                          <component :is="renderMenuIcon(getMenuIconName(grandchild))" />
                        </el-icon>
                        <span class="menu-text-wrapper">
                          {{ grandchild.menuName }}
                          <el-badge 
                            v-if="isApprovalMenu(grandchild) && approvalPendingCount > 0" 
                            :value="approvalPendingCount" 
                            class="menu-badge-inline"
                          />
                        </span>
                      </el-menu-item>
                    </template>
                  </el-sub-menu>
                  
                  <!-- MENU 类型但没有子菜单，渲染为 menu-item -->
                  <el-menu-item 
                    v-else-if="child.menuType === 'MENU' && child.status === 1 && child.visible === 1"
                    :index="getMenuIndex(child)"
                    class="menu-item-with-badge"
                  >
                    <el-icon v-if="getMenuIconName(child)">
                      <component :is="renderMenuIcon(getMenuIconName(child))" />
                    </el-icon>
                    <span class="menu-text-wrapper">
                      {{ child.menuName }}
                      <el-badge 
                        v-if="isApprovalMenu(child) && approvalPendingCount > 0" 
                        :value="approvalPendingCount" 
                        class="menu-badge-inline"
                      />
                    </span>
                  </el-menu-item>
                  
                  <!-- DIRECTORY 类型，渲染为 sub-menu -->
                  <el-sub-menu 
                    v-else-if="child.menuType === 'DIRECTORY' && child.status === 1"
                    :index="`sub-${child.id}`"
                  >
                    <template #title>
                      <el-icon v-if="getMenuIconName(child)">
                        <component :is="renderMenuIcon(getMenuIconName(child))" />
                      </el-icon>
                      <span>{{ child.menuName }}</span>
                    </template>
                    <template v-for="grandchild in child.children" :key="grandchild.id">
                      <el-menu-item 
                        v-if="grandchild.menuType === 'MENU' && grandchild.status === 1 && grandchild.visible === 1"
                        :index="getMenuIndex(grandchild)"
                        class="menu-item-with-badge"
                      >
                        <el-icon v-if="getMenuIconName(grandchild)">
                          <component :is="renderMenuIcon(getMenuIconName(grandchild))" />
                        </el-icon>
                        <span class="menu-text-wrapper">
                          {{ grandchild.menuName }}
                          <el-badge 
                            v-if="isApprovalMenu(grandchild) && approvalPendingCount > 0" 
                            :value="approvalPendingCount" 
                            class="menu-badge-inline"
                          />
                        </span>
                      </el-menu-item>
                    </template>
                  </el-sub-menu>
                </template>
              </el-sub-menu>
            </template>
          </el-menu>
        </el-aside>
        
        <!-- 右侧内容区 -->
        <el-container class="content-container">
          <!-- 标签页栏 -->
          <div class="tabs-container">
            <div class="tabs-wrapper">
              <div
                v-for="tab in tabs"
                :key="tab.path"
                :class="['tab-item', { active: activeTab === tab.path, fixed: tab.fixed }]"
                @click="handleTabClick(tab.path)"
                @contextmenu.prevent="handleTabContextMenu($event, tab.path)"
              >
                <span class="tab-title">{{ tab.title }}</span>
                <el-icon
                  v-if="!tab.fixed"
                  class="tab-close"
                  @click.stop="handleTabClose(tab.path)"
                >
                  <Close />
                </el-icon>
              </div>
            </div>
          </div>
          
          <!-- 标签栏右键菜单 -->
          <div
            v-if="contextMenuVisible"
            class="tab-context-menu"
            :style="{ left: contextMenuX + 'px', top: contextMenuY + 'px' }"
            @click.stop
          >
            <div class="context-menu-item" @click="handleCloseLeft">
              <el-icon><ArrowLeft /></el-icon>
              <span>关闭左侧</span>
            </div>
            <div class="context-menu-item" @click="handleCloseRight">
              <el-icon><ArrowRight /></el-icon>
              <span>关闭右侧</span>
            </div>
            <div class="context-menu-item" @click="handleCloseOthers">
              <el-icon><CircleClose /></el-icon>
              <span>关闭其他</span>
            </div>
            <div class="context-menu-item" @click="handleCloseAll">
              <el-icon><Close /></el-icon>
              <span>全部关闭</span>
            </div>
          </div>
          
          <!-- 主内容区 -->
          <el-main class="main-content">
        <router-view v-slot="{ Component, route }">
          <keep-alive :include="cachedViews">
            <component
              :is="Component"
              :key="route.name"
              v-if="route.path !== '/home' && route.path !== '/'"
            />
          </keep-alive>
        </router-view>
        <div v-if="$route.path === '/home' || $route.path === '/'" class="home-dashboard">
          <!-- 欢迎横幅 -->
          <div :class="['welcome-banner', { 'manager-banner': userInfo.userType === 'MANAGER' }]">
            <div class="banner-content">
              <div class="banner-left">
                <h1 class="welcome-title">
                  <span class="greeting">欢迎回来！</span>
                </h1>
                <p class="welcome-subtitle">
                  <span v-if="userInfo.userType === 'MANAGER'">智拓星销售业务管理系统 - 企业运营管理平台</span>
                  <span v-else-if="userInfo.userType === 'SALESMAN'">智拓星销售业务管理系统 - 业务员工作台</span>
                  <span v-else>智拓星销售业务管理系统 - 让业务管理更高效</span>
                </p>
              </div>
              <div class="banner-right">
                <div class="user-avatar">
                  <el-icon :size="60"><User /></el-icon>
                </div>
              </div>
            </div>
          </div>

          <!-- 统计卡片 - 系统管理员显示 -->
          <div v-if="userInfo.userType === 'SYSTEM_ADMIN'" class="stats-grid">
            <el-card class="stat-card stat-card-primary" shadow="hover" @click="handleSystemAdminStatClick('company')">
              <div class="stat-content">
                <div class="stat-icon primary">
                  <el-icon :size="40"><OfficeBuilding /></el-icon>
                </div>
                <div class="stat-info">
                  <div class="stat-label">公司总数</div>
                  <div class="stat-value">{{ statsData.companyCount !== null ? statsData.companyCount : '--' }}</div>
                </div>
              </div>
            </el-card>
            
            <el-card class="stat-card stat-card-success" shadow="hover" @click="handleSystemAdminStatClick('companyUser')">
              <div class="stat-content">
                <div class="stat-icon success">
                  <el-icon :size="40"><UserFilled /></el-icon>
                </div>
                <div class="stat-info">
                  <div class="stat-label">企业用户总数</div>
                  <div class="stat-value">{{ statsData.companyUserCount !== null ? statsData.companyUserCount : '--' }}</div>
                </div>
              </div>
            </el-card>
            
            <el-card class="stat-card stat-card-warning" shadow="hover" @click="handleSystemAdminStatClick('personalUser')">
              <div class="stat-content">
                <div class="stat-icon warning">
                  <el-icon :size="40"><User /></el-icon>
                </div>
                <div class="stat-info">
                  <div class="stat-label">个人用户总数</div>
                  <div class="stat-value">{{ statsData.personalUserCount !== null ? statsData.personalUserCount : '--' }}</div>
                </div>
              </div>
            </el-card>
            
            <el-card class="stat-card stat-card-info" shadow="hover" @click="handleSystemAdminStatClick('membership')">
              <div class="stat-content">
                <div class="stat-icon info">
                  <el-icon :size="40"><Star /></el-icon>
                </div>
                <div class="stat-info">
                  <div class="stat-label">会员总数</div>
                  <div class="stat-value">{{ statsData.membershipCount !== null ? statsData.membershipCount : '--' }}</div>
                </div>
              </div>
            </el-card>
          </div>

          <!-- 统计卡片 - 管理员显示 -->
          <div v-if="userInfo.userType === 'MANAGER'" class="stats-grid manager-stats-grid">
            <el-card class="stat-card manager-stat-card-primary" shadow="hover" @click="handleManagerStatClick('customer')">
              <div class="stat-content">
                <div class="stat-icon manager-icon-primary">
                  <el-icon :size="40"><UserFilled /></el-icon>
                </div>
                <div class="stat-info">
                  <div class="stat-label">客户总数</div>
                  <div class="stat-value">{{ managerStatsData.customerCount !== null ? managerStatsData.customerCount : '--' }}</div>
                </div>
              </div>
            </el-card>
            
            <el-card class="stat-card manager-stat-card-info" shadow="hover" @click="handleManagerStatClick('monthNewCustomer')">
              <div class="stat-content">
                <div class="stat-icon manager-icon-info">
                  <el-icon :size="40"><TrendCharts /></el-icon>
                </div>
                <div class="stat-info">
                  <div class="stat-label">本月新增客户</div>
                  <div class="stat-value">{{ managerStatsData.monthNewCustomer !== null ? managerStatsData.monthNewCustomer : '--' }}</div>
                </div>
              </div>
            </el-card>
            
            <el-card class="stat-card manager-stat-card-warning" shadow="hover" @click="handleManagerStatClick('pending')">
              <div class="stat-content">
                <div class="stat-icon manager-icon-warning">
                  <el-icon :size="40"><Check /></el-icon>
                </div>
                <div class="stat-info">
                  <div class="stat-label">待审批</div>
                  <div class="stat-value">{{ managerStatsData.pendingApprovalCount !== null ? managerStatsData.pendingApprovalCount : '--' }}</div>
                </div>
              </div>
            </el-card>
            
            <el-card class="stat-card manager-stat-card-success" shadow="hover" @click="handleManagerStatClick('approving')">
              <div class="stat-content">
                <div class="stat-icon manager-icon-success">
                  <el-icon :size="40"><Clock /></el-icon>
                </div>
                <div class="stat-info">
                  <div class="stat-label">审批中</div>
                  <div class="stat-value">{{ managerStatsData.approvingCount !== null ? managerStatsData.approvingCount : '--' }}</div>
                </div>
              </div>
            </el-card>
          </div>

          <!-- 统计卡片 - 业务员显示 -->
          <div v-if="userInfo.userType === 'SALESMAN'" class="stats-grid salesman-stats-grid">
            <el-card class="stat-card salesman-stat-card-primary" shadow="hover" @click="handleQuickAction('customer')">
              <div class="stat-content">
                <div class="stat-icon salesman-icon-primary">
                  <el-icon :size="40"><UserFilled /></el-icon>
                </div>
                <div class="stat-info">
                  <div class="stat-label">我的客户</div>
                  <div class="stat-value">{{ salesmanStatsData.customerCount !== null ? salesmanStatsData.customerCount : '--' }}</div>
                </div>
              </div>
            </el-card>
            
            <el-card class="stat-card salesman-stat-card-success" shadow="hover" @click="handleQuickAction('contract')">
              <div class="stat-content">
                <div class="stat-icon salesman-icon-success">
                  <el-icon :size="40"><Document /></el-icon>
                </div>
                <div class="stat-info">
                  <div class="stat-label">我的合同</div>
                  <div class="stat-value">{{ salesmanStatsData.contractCount !== null ? salesmanStatsData.contractCount : '--' }}</div>
                </div>
              </div>
            </el-card>
            
            <el-card class="stat-card salesman-stat-card-warning" shadow="hover" @click="handleQuickAction('follow')">
              <div class="stat-content">
                <div class="stat-icon salesman-icon-warning">
                  <el-icon :size="40"><ChatLineRound /></el-icon>
                </div>
                <div class="stat-info">
                  <div class="stat-label">跟进记录</div>
                  <div class="stat-value">{{ salesmanStatsData.followCount !== null ? salesmanStatsData.followCount : '--' }}</div>
                </div>
              </div>
            </el-card>
            
            <el-card class="stat-card salesman-stat-card-info" shadow="hover" @click="handleQuickAction('deal')">
              <div class="stat-content">
                <div class="stat-icon salesman-icon-info">
                  <el-icon :size="40"><Money /></el-icon>
                </div>
                <div class="stat-info">
                  <div class="stat-label">成交金额</div>
                  <div class="stat-value">¥{{ formatMoney(salesmanStatsData.totalDealAmount || 0) }}</div>
                </div>
              </div>
            </el-card>
          </div>

          <!-- 快捷操作 - 业务员显示 -->
          <div v-if="userInfo.userType === 'SALESMAN'" class="quick-actions-grid">
            <el-card class="quick-action-card" shadow="hover" @click="handleQuickAction('customer')">
              <div class="quick-action-content">
                <el-icon class="quick-action-icon" :size="32"><UserFilled /></el-icon>
                <div class="quick-action-label">客户管理</div>
              </div>
            </el-card>
            <el-card class="quick-action-card" shadow="hover" @click="handleQuickAction('contract')">
              <div class="quick-action-content">
                <el-icon class="quick-action-icon" :size="32"><Document /></el-icon>
                <div class="quick-action-label">合同管理</div>
              </div>
            </el-card>
            <el-card class="quick-action-card" shadow="hover" @click="handleQuickAction('follow')">
              <div class="quick-action-content">
                <el-icon class="quick-action-icon" :size="32"><ChatLineRound /></el-icon>
                <div class="quick-action-label">跟进管理</div>
              </div>
            </el-card>
            <el-card class="quick-action-card" shadow="hover" @click="handleQuickAction('report')">
              <div class="quick-action-content">
                <el-icon class="quick-action-icon" :size="32"><DataAnalysis /></el-icon>
                <div class="quick-action-label">数据报表</div>
              </div>
            </el-card>
            <el-card class="quick-action-card" shadow="hover" @click="handleQuickAction('travel')">
              <div class="quick-action-content">
                <el-icon class="quick-action-icon" :size="32"><CreditCard /></el-icon>
                <div class="quick-action-label">报销申请</div>
              </div>
            </el-card>
            <el-card class="quick-action-card" shadow="hover" @click="handleQuickAction('trip')">
              <div class="quick-action-content">
                <el-icon class="quick-action-icon" :size="32"><Location /></el-icon>
                <div class="quick-action-label">出差申请</div>
              </div>
            </el-card>
          </div>

          <!-- 主要内容区 -->
          <div class="main-grid">
            <!-- 左侧：企业信息/个人信息 -->
            <div class="left-column">
              <!-- 企业信息卡片 - 仅管理员显示 -->
              <el-card v-if="userInfo.userType === 'MANAGER'" class="info-card" shadow="hover">
                <template #header>
                  <div class="card-header">
                    <el-icon><OfficeBuilding /></el-icon>
                    <span>企业信息</span>
                  </div>
                </template>
                <div class="user-info-list">
                  <div class="info-item">
                    <span class="info-label">企业名称</span>
                    <span class="info-value">{{ companyInfo.companyName || '--' }}</span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">企业编码</span>
                    <span class="info-value">{{ companyInfo.companyCode || '--' }}</span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">企业简称</span>
                    <span class="info-value">{{ companyInfo.companyShortName || '--' }}</span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">统一社会信用代码</span>
                    <span class="info-value">{{ companyInfo.creditCode || '--' }}</span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">联系电话</span>
                    <span class="info-value">{{ companyInfo.phone || '--' }}</span>
                  </div>
                </div>
              </el-card>

              <!-- 用户信息卡片 - 业务员显示 -->
              <el-card v-if="userInfo.userType === 'SALESMAN'" class="info-card" shadow="hover">
                <template #header>
                  <div class="card-header">
                    <el-icon><User /></el-icon>
                    <span>个人信息</span>
                  </div>
                </template>
                <div class="user-info-list">
                  <div class="info-item">
                    <span class="info-label">用户名</span>
                    <span class="info-value">{{ userInfo.username || '--' }}</span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">真实姓名</span>
                    <span class="info-value">{{ userInfo.realName || '--' }}</span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">用户类型</span>
                    <el-tag type="success" size="small">业务员</el-tag>
                  </div>
                </div>
              </el-card>

              <!-- 用户信息卡片 - 系统管理员和其他角色显示 -->
              <el-card v-else class="info-card" shadow="hover">
                <template #header>
                  <div class="card-header">
                    <el-icon><User /></el-icon>
                    <span>个人信息</span>
                  </div>
                </template>
                <div class="user-info-list">
                  <div class="info-item">
                    <span class="info-label">用户名</span>
                    <span class="info-value">{{ userInfo.username || '--' }}</span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">真实姓名</span>
                    <span class="info-value">{{ userInfo.realName || '--' }}</span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">用户类型</span>
                    <el-tag :type="getUserTypeTagType(userInfo.userType)" size="small">
                      {{ getUserTypeName(userInfo.userType) }}
                    </el-tag>
                  </div>
                </div>
              </el-card>
            </div>

            <!-- 右侧：使用帮助 -->
            <div class="right-column">
              <el-card class="info-card" shadow="hover">
                <template #header>
                  <div class="card-header">
                    <el-icon><QuestionFilled /></el-icon>
                    <span>使用帮助</span>
                  </div>
                </template>
                <div class="help-content">
                  <template v-if="userInfo.userType === 'MANAGER'">
                    <div class="help-item">
                      <strong>客户管理：</strong>查看和管理企业客户信息，跟踪客户状态
                    </div>
                    <div class="help-item">
                      <strong>数据报表：</strong>查看销售数据、业务员业绩等统计分析
                    </div>
                    <div class="help-item">
                      <strong>审批管理：</strong>处理业务员提交的各类审批申请
                    </div>
                    <div class="help-item">
                      <strong>账号管理：</strong>管理企业内业务员、内勤、会计等账号
                    </div>
                  </template>
                  <template v-else-if="userInfo.userType === 'SALESMAN'">
                    <div class="help-item">
                      <strong>客户管理：</strong>管理我的客户信息，支持导入导出功能
                    </div>
                    <div class="help-item">
                      <strong>合同管理：</strong>创建和管理销售合同，跟踪合同状态
                    </div>
                    <div class="help-item">
                      <strong>跟进管理：</strong>记录客户跟进情况，维护客户关系
                    </div>
                    <div class="help-item">
                      <strong>数据报表：</strong>查看我的业务数据统计和趋势分析
                    </div>
                  </template>
                  <template v-else>
                    <div class="help-item">
                      <strong>功能导航：</strong>点击左侧菜单可快速访问各功能模块
                    </div>
                    <div class="help-item">
                      <strong>标签管理：</strong>顶部标签栏显示已打开的页面，可快速切换
                    </div>
                    <div class="help-item">
                      <strong>数据管理：</strong>支持数据的增删改查和批量操作
                    </div>
                    <div class="help-item">
                      <strong>权限控制：</strong>不同角色拥有不同的操作权限
                    </div>
                  </template>
                </div>
              </el-card>
            </div>
          </div>
        </div>

        <!-- 右下角一键回到顶部按钮 -->
        <el-backtop
          :right="40"
          :bottom="40"
          :visibility-height="100"
          class="home-backtop"
        >
          <div class="backtop-inner">
            <el-icon :size="18">
              <ArrowUp />
            </el-icon>
          </div>
        </el-backtop>
        </el-main>
        </el-container>
      </el-container>
    </el-container>
  </div>
</template>

<script>
export default {
  name: 'HomePage'
}
</script>

<script setup>
import { ref, reactive, onMounted, computed, onUnmounted, watch, nextTick, provide } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { hasRoutePermission } from '../utils/permission'
import { ElMessage, ElMessageBox } from 'element-plus'
import { 
  Close, 
  HomeFilled, 
  User, 
  UserFilled,
  QuestionFilled,
  OfficeBuilding,
  Star,
  Check,
  TrendCharts,
  Document,
  ChatLineRound,
  Money,
  DataAnalysis,
  CreditCard,
  Location,
  Message,
  Clock,
  ArrowLeft,
  ArrowRight,
  CircleClose,
  ArrowUp
} from '@element-plus/icons-vue'
import * as ElementPlusIconsVue from '@element-plus/icons-vue'
import { logout, changePassword, verifyPassword } from '../api/auth'
import { getUserMenuTree } from '../api/sysMenu'
import { getAllCompanies } from '../api/company'
import { getUserList } from '../api/sysUser'
import { getMembershipList } from '../api/membership'
import { getApprovalList } from '../api/managerApproval'
import { getCustomerCount as getManagerCustomerCount } from '../api/managerCustomer'
import { getManagerDashboard } from '../api/managerReport'
import { getCurrentCompany } from '../api/managerCompany'
import { getCustomerCount, getContractCount, getFollowStatistics, getDealRecordList } from '../api/salesman'
import { getUnreadMessageCount } from '../api/message'

const router = useRouter()
const route = useRoute()
const menuRef = ref(null)
const activeMenuIndex = ref('')
const userInfo = ref({
  userId: '',
  username: '',
  realName: '',
  userType: ''
})

// 菜单收起/展开状态
const isCollapse = ref(false)

// 菜单数据
const menuList = ref([])

// 统计数据（系统管理员）
const statsData = ref({
  companyCount: null,
  companyUserCount: null,
  personalUserCount: null,
  membershipCount: null
})

// 统计数据（管理员）
const managerStatsData = ref({
  customerCount: null,
  pendingApprovalCount: null,
  approvingCount: null,
  salesmanCount: null,
  monthNewCustomer: null
})

// 统计数据（业务员）
const salesmanStatsData = ref({
  customerCount: null,
  contractCount: null,
  followCount: null,
  totalDealAmount: null
})

// 企业信息（管理员）
const companyInfo = ref({
  companyName: '',
  companyCode: '',
  companyShortName: '',
  creditCode: '',
  phone: ''
})

// 修改密码对话框
const changePasswordDialogVisible = ref(false)
const changePasswordFormRef = ref(null)
const changePasswordForm = reactive({
  oldPassword: '',
  newPassword: '',
  confirmPassword: ''
})

const changePasswordRules = {
  oldPassword: [
    { required: true, message: '请输入原密码', trigger: 'blur' }
  ],
  newPassword: [
    { required: true, message: '请输入新密码', trigger: 'blur' },
    { min: 6, max: 20, message: '新密码长度应在6-20位之间', trigger: 'blur' }
  ],
  confirmPassword: [
    { required: true, message: '请再次输入新密码', trigger: 'blur' },
    {
      validator: (rule, value, callback) => {
        if (!value) {
          callback(new Error('请再次输入新密码'))
        } else if (value !== changePasswordForm.newPassword) {
          callback(new Error('两次输入的新密码不一致'))
        } else {
          callback()
        }
      },
      trigger: 'blur'
    }
  ]
}

const changePasswordLoading = ref(false)

const openChangePasswordDialog = () => {
  changePasswordForm.oldPassword = ''
  changePasswordForm.newPassword = ''
  changePasswordForm.confirmPassword = ''
  changePasswordDialogVisible.value = true
}

const handleChangePassword = () => {
  if (!changePasswordFormRef.value) return
  changePasswordFormRef.value.validate(async (valid) => {
    if (!valid) return
    
    // 验证新密码不能与原密码相同
    if (changePasswordForm.oldPassword === changePasswordForm.newPassword) {
      ElMessageBox.alert('新密码不能与原密码相同', '提示', {
        confirmButtonText: '确定',
        type: 'warning'
      })
      return
    }
    
    changePasswordLoading.value = true
    try {
      // 可选：先验证原密码是否正确
      await verifyPassword(changePasswordForm.oldPassword)
      await changePassword({
        oldPassword: changePasswordForm.oldPassword,
        newPassword: changePasswordForm.newPassword,
        confirmPassword: changePasswordForm.confirmPassword
      })
      ElMessage.success('密码修改成功，请使用新密码重新登录')
      changePasswordDialogVisible.value = false
      // 修改密码后强制退出登录
      await handleLogout()
    } catch (error) {
      console.error('修改密码失败:', error)
      ElMessage.error(error?.response?.data?.msg || error.message || '修改密码失败')
    } finally {
      changePasswordLoading.value = false
    }
  })
}

// 待审批数量（用于菜单角标）
const approvalPendingCount = ref(0)

// 未读消息数量
const unreadMessageCount = ref(0)

// 定时器引用
let approvalCountInterval = null
let cacheRefreshInterval = null
let unreadMessageCountInterval = null

// 缓存视图列表（用于 keep-alive）
const cachedViews = ref([])

// 标签页列表
const tabs = ref([
  {
    path: '/home',
    title: '首页',
    fixed: true
  }
])

// 当前激活的标签
const activeTab = computed(() => {
  const path = route.path
  // 对于合同表单页面，需要根据查询参数生成唯一的路径标识
  if (path === '/home/salesman-contract-form') {
    const query = route.query
    return query && query.id ? `${path}?id=${query.id}` : path
  }
  return path
})

// 判断菜单是否是商品管理菜单（通过路径或菜单名称）
const isProductManagementMenu = (menu) => {
  if (!menu) return false
  
  // 通过菜单名称判断
  if (menu.menuName === '商品管理') {
    return true
  }
  
  // 通过路径判断
  if (menu.path) {
    let path = menu.path
    if (!path.startsWith('/home')) {
      path = path.startsWith('/') ? `/home${path}` : `/home/${path}`
    }
    if (path === '/home/salesman-product' || 
        path === '/home/internal-product' ||
        path === '/home/assistant/product' ||
        (path.includes('salesman') && path.includes('product'))) {
      return true
    }
  }
  
  return false
}

// 递归过滤子菜单中的商品管理（业务员个人用户）
const filterProductManagementFromChildren = (children) => {
  if (!children || !Array.isArray(children)) {
    return children
  }
  
  return children.filter(child => {
    if (!child) return false
    
    // 如果是商品管理菜单，过滤掉
    if (isProductManagementMenu(child)) {
      return false
    }
    
    // 如果有子菜单，递归过滤
    if (child.children && Array.isArray(child.children) && child.children.length > 0) {
      child.children = filterProductManagementFromChildren(child.children)
    }
    
    return true
  })
}

// 判断菜单是否有可见的子菜单（递归检查）
const hasVisibleChildren = (menu) => {
  if (!menu || !menu.children || !Array.isArray(menu.children) || menu.children.length === 0) {
    return false
  }
  
  // 递归检查子菜单是否有符合条件的菜单
  for (const child of menu.children) {
    if (!child) continue
    // 如果是菜单类型且可见，返回 true
    if (child.menuType === 'MENU' && child.status === 1 && child.visible === 1) {
      return true
    }
    // 如果是目录类型，递归检查其子菜单
    if (child.menuType === 'DIRECTORY' && child.status === 1) {
      if (hasVisibleChildren(child)) {
        return true
      }
    }
  }
  
  return false
}

// 深拷贝菜单对象（用于避免修改原始数据）
const deepCloneMenu = (menu) => {
  if (!menu) return null
  const cloned = { ...menu }
  if (menu.children && Array.isArray(menu.children)) {
    cloned.children = menu.children.map(child => deepCloneMenu(child)).filter(Boolean)
  }
  return cloned
}

// 处理后的菜单列表（计算属性）
const processedMenuList = computed(() => {
  if (!menuList.value || !Array.isArray(menuList.value)) {
    return []
  }
  
  // 获取用户信息中的企业ID
  const hasCompanyId = userInfo.value && userInfo.value.companyId
  const isSalesmanWithoutCompany = userInfo.value && userInfo.value.userType === 'SALESMAN' && !hasCompanyId
  
  return menuList.value.map(menu => {
    // 深拷贝菜单，避免修改原始数据
    const clonedMenu = deepCloneMenu(menu)
    if (!clonedMenu) return null
    
    // 如果是业务员个人用户，需要过滤子菜单中的商品管理
    if (isSalesmanWithoutCompany && clonedMenu.children && Array.isArray(clonedMenu.children)) {
      clonedMenu.children = filterProductManagementFromChildren(clonedMenu.children)
    }
    
    return clonedMenu
  }).filter(menu => {
    if (!menu) return false

    // 特殊处理：消息中心只通过右上角信封入口展示，不在左侧菜单显示
    // 1) 通过 menuCode 判断
    if (menu.menuCode === 'MESSAGE_CENTER') {
      return false
    }
    // 2) 通过 path 判断（兼容不同配置方式）
    if (menu.path) {
      let path = menu.path
      if (!path.startsWith('/home')) {
        path = path.startsWith('/') ? `/home${path}` : `/home/${path}`
      }
      if (path === '/home/message-center') {
        return false
      }
    }
    
    // 业务员如果是个人用户（没有企业ID），隐藏商品管理菜单（通过路径或菜单名称判断）
    if (isSalesmanWithoutCompany && isProductManagementMenu(menu)) {
      return false
    }

    // 只显示状态为 1 的菜单
    if (menu.status !== 1) return false
    
    // 目录类型：需要检查是否有可见子菜单
    if (menu.menuType === 'DIRECTORY') {
      return hasVisibleChildren(menu)
    }
    
    // 菜单类型：需要 visible 为 1
    return menu.visible === 1
  })
})

// 获取菜单应使用的图标名称（支持根据路径设置默认图标）
const getMenuIconName = (menu) => {
  if (!menu) return ''
  if (menu.icon) return menu.icon

  const index = getMenuIndex(menu)
  const menuName = menu.menuName || ''
  
  // 内勤 - 发货单管理：根据路径或菜单名称匹配，默认使用文档图标（Document）
  if (index === '/home/internal-delivery' || 
      index === '/home/assistant/delivery' || 
      index === '/home/assistant-delivery' ||
      menuName === '发货单管理' ||
      menuName === '发货管理') {
    return 'Document'
  }
  // 其他菜单暂不强制指定默认图标
  return ''
}

// 获取菜单项的 index 值（统一处理路径）
const getMenuIndex = (menu) => {
  if (!menu) {
    console.warn('getMenuIndex: menu 为空')
    return null
  }
  
  // 优先使用 path
  if (menu.path) {
    let path = menu.path
    
    // 调试：如果是仓库管理，输出日志
    if (path.includes('warehouse') || menu.menuName?.includes('仓库')) {
      console.log('getMenuIndex - 仓库管理菜单:', { path, menuName: menu.menuName, menuCode: menu.menuCode, id: menu.id })
    }
    
    // 确保路径以 /home 开头
    if (!path.startsWith('/home')) {
      // 如果 path 不以 /home 开头，添加前缀
      if (path.startsWith('/')) {
        path = `/home${path}`
      } else {
        path = `/home/${path}`
      }
    }

    // 针对 manager / internal / assistant 等路径的显式映射，避免与路由不一致
    if (path === '/home/manager/account' || path === '/home/manager/user') {
      return '/home/manager-user'
    }
    if (path === '/home/manager/company') {
      return '/home/manager-company'
    }
    if (path === '/home/manager/report') {
      return '/home/manager-report'
    }
    if (path === '/home/manager/customer') {
      return '/home/manager-customer'
    }
    // 审批管理（管理员）：兼容 /home/manager/approval 与 /home/manager-approval
    if (path === '/home/manager/approval' || path === '/home/manager-approval') {
      return '/home/manager-approval'
    }
    // 审批管理（内勤）：后端可能配置为 /assistant/approval 或 /internal/approval
    // 统一映射到前端路由 /home/internal-approval
    if (
      path === '/home/assistant/approval' ||
      path === '/home/assistant-approval' ||
      path === '/home/internal/approval' ||
      path === '/home/internal-approval'
    ) {
      return '/home/internal-approval'
    }
    
    // 仓库管理：兼容 /home/assistant/warehouse 与 /home/internal-warehouse
    if (
      path === '/home/assistant/warehouse' ||
      path === '/home/assistant-warehouse' ||
      path === '/home/internal/warehouse' ||
      path === '/home/internal-warehouse'
    ) {
      return '/home/internal-warehouse'
    }
    
    // 商品管理：兼容 /home/assistant/product 与 /home/internal-product，以及业务员的 /home/salesman-product
    if (
      path === '/home/assistant/product' ||
      path === '/home/assistant-product' ||
      path === '/home/internal/product' ||
      path === '/home/internal-product' ||
      path === '/home/salesman-product'
    ) {
      // 业务员访问时返回业务员路径，内勤访问时返回内勤路径
      if (path === '/home/salesman-product') {
        return '/home/salesman-product'
      }
      return '/home/internal-product'
    }
    
    // 发货管理：兼容 /home/assistant/delivery 与 /home/internal-delivery
    if (
      path === '/home/assistant/delivery' ||
      path === '/home/assistant-delivery' ||
      path === '/home/internal/delivery' ||
      path === '/home/internal-delivery'
    ) {
      return '/home/internal-delivery'
    }
    
    // 采购管理：兼容 /home/assistant/purchase 与 /home/internal-purchase
    if (
      path === '/home/assistant/purchase' ||
      path === '/home/assistant-purchase' ||
      path === '/home/internal/purchase' ||
      path === '/home/internal-purchase'
    ) {
      return '/home/internal-purchase'
    }
    
    // 将路径中的斜杠转换为连字符（例如：/home/manager/company -> /home/manager-company）
    // 但保留 /home 前缀
    if (path.includes('/manager/') || path.includes('/salesman/') || path.includes('/internal/')) {
      path = path.replace(/\/(manager|salesman|internal)\//g, '/$1-')
    }
    
    // 处理 /assistant/ 路径，统一转换为 /internal- 格式
    if (path.includes('/assistant/')) {
      path = path.replace(/\/assistant\//g, '/internal-')
    }
    
    // 调试：如果是仓库管理相关的路径，输出日志
    if (path.includes('warehouse')) {
      console.log('仓库管理菜单路径转换:', menu.path, '->', path)
    }
    
    return path
  }
  
  // 其次使用 menuCode
  if (menu.menuCode) {
    return `/home/${menu.menuCode}`
  }
  
  return null
}

// 递归查找菜单路径（支持多种匹配方式）
const findMenuPath = (menus, targetPath) => {
  if (!menus || menus.length === 0) return null
  
  for (const menu of menus) {
    // 使用统一的路径获取方法
    const menuPath = getMenuIndex(menu)
    if (!menuPath) continue
    
    // 精确匹配
    if (menuPath === targetPath) {
      return menuPath
    }
    
    // 路径包含匹配（处理子路由情况）
    if (targetPath.startsWith(menuPath + '/') || targetPath === menuPath) {
      return menuPath
    }
    
    // 检查子菜单
    if (menu.children && menu.children.length > 0) {
      const childMatch = findMenuPath(menu.children, targetPath)
      if (childMatch) {
        return childMatch
      }
    }
  }
  return null
}

// 收集所有菜单项的路径（用于匹配）
const getAllMenuPaths = () => {
  const allMenuItems = []
  const collectMenuItems = (menus) => {
    if (!menus || menus.length === 0) return
    for (const menu of menus) {
      if (menu.menuType === 'MENU') {
        const itemPath = getMenuIndex(menu)
        if (itemPath) {
          allMenuItems.push({ path: itemPath, menu })
        }
      }
      if (menu.children && menu.children.length > 0) {
        collectMenuItems(menu.children)
      }
    }
  }
  collectMenuItems(menuList.value)
  return allMenuItems
}

// 当前激活的菜单
const activeMenu = computed(() => {
  let currentPath = route.path

  // 统一处理一些历史路径兼容（例如出差申请）和路由别名
  const aliasMap = {
    '/home/salesman-business-trip': '/home/salesman-trip',
    // 内勤模块路由别名映射
    '/home/assistant/warehouse': '/home/internal-warehouse',
    '/home/assistant/product': '/home/internal-product',
    '/home/assistant/delivery': '/home/internal-delivery',
    '/home/assistant/purchase': '/home/internal-purchase',
    '/home/assistant/approval': '/home/internal-approval'
  }
  if (aliasMap[currentPath]) {
    currentPath = aliasMap[currentPath]
  }
  
  // 如果是 /home，直接返回
  if (currentPath === '/home' || currentPath === '/') {
    return '/home'
  }
  
  // 获取所有菜单项路径
  const allMenuItems = getAllMenuPaths()
  
  // 特殊处理：如果是审批详情页，应该保持对应的审批列表页菜单激活状态
  if (currentPath.startsWith('/home/approval-detail/')) {
    // 检查标签页中是否有审批列表页的标签，如果有则保持该菜单激活
    const approvalListTab = tabs.value.find(tab => 
      tab.path === '/home/internal-approval' || 
      tab.path === '/home/manager-approval' ||
      tab.path.startsWith('/home/internal-approval') ||
      tab.path.startsWith('/home/manager-approval')
    )
    if (approvalListTab) {
      // 提取基础路径（去掉查询参数）
      const basePath = approvalListTab.path.split('?')[0]
      console.log('审批详情页，保持审批列表菜单激活:', basePath)
      // 验证这个路径是否在菜单中存在
      const menuMatch = allMenuItems.find(item => item.path === basePath)
      if (menuMatch) {
        return basePath
      }
    }
    // 如果没有找到标签，尝试根据权限判断应该激活哪个菜单
    // 优先检查内勤审批管理
    if (hasRoutePermission('/home/internal-approval')) {
      const internalMatch = allMenuItems.find(item => item.path === '/home/internal-approval')
      if (internalMatch) {
        console.log('审批详情页，激活内勤审批管理菜单')
        return '/home/internal-approval'
      }
    }
    // 其次检查管理员审批管理
    if (hasRoutePermission('/home/manager-approval')) {
      const managerMatch = allMenuItems.find(item => item.path === '/home/manager-approval')
      if (managerMatch) {
        console.log('审批详情页，激活管理员审批管理菜单')
        return '/home/manager-approval'
      }
    }
  }
  
  // 1. 精确匹配
  const exactMatch = allMenuItems.find(item => item.path === currentPath)
  if (exactMatch) {
    console.log('菜单选中 - 精确匹配:', exactMatch.path)
    return exactMatch.path
  }
  
  // 2. 使用 findMenuPath 递归查找（处理子路由）
  const matchedPath = findMenuPath(menuList.value, currentPath)
  if (matchedPath) {
    console.log('菜单选中 - 递归匹配:', matchedPath, '当前路径:', currentPath)
    return matchedPath
  }
  
  // 2.5. 特殊处理：仓库管理等内勤模块的路径匹配（在递归匹配之后，模糊匹配之前）
  // 因为后端菜单配置可能使用不同的路径格式，需要特殊处理
  if (currentPath === '/home/internal-warehouse' || currentPath === '/home/assistant/warehouse') {
    console.log('仓库管理路径匹配调试:')
    console.log('当前路径:', currentPath)
    console.log('所有菜单项路径:', allMenuItems.map(item => ({ path: item.path, menu: item.menu })))
    
    // 尝试多种匹配方式
    // 1. 精确匹配所有可能的路径格式
    let warehouseMatch = allMenuItems.find(item => {
      const itemPath = item.path
      return itemPath === '/home/internal-warehouse' || 
             itemPath === '/home/assistant/warehouse' ||
             itemPath === '/home/internal/warehouse'
    })
    
    // 2. 如果精确匹配失败，尝试模糊匹配（包含 warehouse 关键字）
    if (!warehouseMatch) {
      warehouseMatch = allMenuItems.find(item => {
        const itemPath = item.path.toLowerCase()
        return itemPath.includes('warehouse')
      })
    }
    
    if (warehouseMatch) {
      console.log('仓库管理 - 匹配成功:', warehouseMatch.path, '当前路径:', currentPath)
      return warehouseMatch.path
    } else {
      console.warn('仓库管理 - 匹配失败，当前路径:', currentPath)
      console.warn('所有菜单项路径:', allMenuItems.map(item => item.path))
    }
  }
  
  // 特殊处理：商品管理等内勤模块路径的匹配（包括业务员商品管理）
  if (currentPath === '/home/internal-product' || currentPath === '/home/assistant/product' || currentPath === '/home/salesman-product') {
    const productMatch = allMenuItems.find(item => {
      const pathLower = item.path.toLowerCase()
      // 业务员访问时，匹配业务员商品管理路径或内勤商品管理路径
      if (currentPath === '/home/salesman-product') {
        return pathLower === '/home/salesman-product' ||
               (pathLower.includes('salesman') && pathLower.includes('product')) ||
               (pathLower.includes('internal') && pathLower.includes('product')) ||
               (pathLower.includes('assistant') && pathLower.includes('product'))
      }
      // 内勤访问时，匹配内勤商品管理路径
      return (pathLower.includes('internal') && pathLower.includes('product')) ||
             (pathLower.includes('assistant') && pathLower.includes('product')) ||
             pathLower === '/home/internal-product' ||
             pathLower === '/home/assistant/product'
    })
    if (productMatch) {
      console.log('商品管理 - 特殊匹配:', productMatch.path)
      return productMatch.path
    }
  }
  
  // 特殊处理：发货管理等内勤模块路径的匹配
  if (currentPath === '/home/internal-delivery' || currentPath === '/home/assistant/delivery') {
    const deliveryMatch = allMenuItems.find(item => {
      const pathLower = item.path.toLowerCase()
      return (pathLower.includes('internal') && pathLower.includes('delivery')) ||
             (pathLower.includes('assistant') && pathLower.includes('delivery')) ||
             pathLower === '/home/internal-delivery' ||
             pathLower === '/home/assistant/delivery'
    })
    if (deliveryMatch) {
      console.log('发货管理 - 特殊匹配:', deliveryMatch.path)
      return deliveryMatch.path
    }
  }
  
  // 特殊处理：采购管理等内勤模块路径的匹配
  if (currentPath === '/home/internal-purchase' || currentPath === '/home/assistant/purchase') {
    const purchaseMatch = allMenuItems.find(item => {
      const pathLower = item.path.toLowerCase()
      return (pathLower.includes('internal') && pathLower.includes('purchase')) ||
             (pathLower.includes('assistant') && pathLower.includes('purchase')) ||
             pathLower === '/home/internal-purchase' ||
             pathLower === '/home/assistant/purchase'
    })
    if (purchaseMatch) {
      console.log('采购管理 - 特殊匹配:', purchaseMatch.path)
      return purchaseMatch.path
    }
  }
  
  // 3. 特殊处理：审批列表页路径的匹配（确保内勤和管理员的审批列表都能正确匹配）
  // 因为后端菜单配置可能使用不同的路径格式（如 /home/assistant/approval），需要特殊处理
  if (currentPath === '/home/internal-approval' || currentPath === '/home/manager-approval') {
    // 如果当前路径是 /home/manager-approval，但用户是内勤（没有管理员权限），应该匹配到内勤审批管理
    if (currentPath === '/home/manager-approval' && !hasRoutePermission('/home/manager-approval') && hasRoutePermission('/home/internal-approval')) {
      const internalMatch = allMenuItems.find(item => {
        const pathLower = item.path.toLowerCase()
        return pathLower.includes('internal') && pathLower.includes('approval') ||
               pathLower.includes('assistant') && pathLower.includes('approval')
      })
      if (internalMatch) {
        console.log('审批列表页 - 管理员路径但用户是内勤，匹配到内勤菜单:', internalMatch.path)
        return internalMatch.path
      }
    }
    
    // 尝试在菜单中查找包含 'approval' 的菜单项
    const approvalMatch = allMenuItems.find(item => {
      const pathLower = item.path.toLowerCase()
      // 匹配内勤审批管理
      if (currentPath === '/home/internal-approval') {
        return pathLower.includes('internal') && pathLower.includes('approval') ||
               pathLower.includes('assistant') && pathLower.includes('approval')
      }
      // 匹配管理员审批管理
      if (currentPath === '/home/manager-approval') {
        return pathLower.includes('manager') && pathLower.includes('approval')
      }
      return false
    })
    if (approvalMatch) {
      console.log('审批列表页 - 特殊匹配:', approvalMatch.path)
      return approvalMatch.path
    }
  }
  
  // 4. 模糊匹配：检查当前路径是否包含菜单路径，或菜单路径是否包含当前路径
  for (const item of allMenuItems) {
    if (currentPath.startsWith(item.path) || item.path.startsWith(currentPath)) {
      console.log('菜单选中 - 模糊匹配:', item.path)
      return item.path
    }
  }
  
  // 4. 特殊处理：审批流配置管理的路径可能不同
  // 支持多种路径变体：approval-flow-config, workflow, approval_flow_config 等
  if (currentPath === '/home/approval-flow-config' || currentPath === '/home/workflow') {
    // 尝试查找包含 'approval' 或 'workflow' 的菜单项（不区分大小写）
    const approvalMatch = allMenuItems.find(item => {
      const pathLower = item.path.toLowerCase()
      return pathLower.includes('approval') || 
             pathLower.includes('workflow')
    })
    if (approvalMatch) {
      console.log('菜单选中 - 审批流匹配:', approvalMatch.path)
      return approvalMatch.path
    }
    
    // 如果还是找不到，尝试匹配 menuCode
    const findMenuByCode = (menus) => {
      for (const menu of menus) {
        if (menu.menuCode) {
          const codeLower = menu.menuCode.toLowerCase()
          if (codeLower.includes('approval') || codeLower.includes('workflow')) {
            return menu
          }
        }
        if (menu.children && menu.children.length > 0) {
          const found = findMenuByCode(menu.children)
          if (found) return found
        }
      }
      return null
    }
    const menuCodeMatch = findMenuByCode(menuList.value)
    if (menuCodeMatch) {
      const matchedPath = getMenuIndex(menuCodeMatch)
      if (matchedPath) {
        console.log('菜单选中 - menuCode匹配:', matchedPath)
        return matchedPath
      }
    }
  }
  
  // 5. 最后，直接使用当前路径（兼容处理）
  console.log('菜单选中 - 使用当前路径:', currentPath)
  return currentPath
})

// 渲染菜单图标 - 返回图标组件
const renderMenuIcon = (iconName) => {
  if (!iconName) {
    return null
  }
  
  // 处理常见的图标名称映射
  const iconMap = {
    'home': 'HomeFilled',
    'setting': 'Setting',
    'user': 'User',
    'menu': 'Menu',
    'lock': 'Lock',
    'document': 'Document',
    'folder': 'Folder',
    'edit': 'Edit',
    'delete': 'Delete',
    'search': 'Search',
    'plus': 'Plus',
    'minus': 'Minus',
    'check': 'Check',
    'close': 'Close',
    'arrow-right': 'ArrowRight',
    'arrow-left': 'ArrowLeft',
    'arrow-up': 'ArrowUp',
    'arrow-down': 'ArrowDown',
    'timer': 'Timer',
    'clock': 'Clock',
    'truck': 'Truck',
    'box': 'Box',
    'files': 'Files',
    'postcard': 'Postcard'
  }
  
  // 先检查映射表
  let iconKey = iconMap[iconName.toLowerCase()]
  
  // 如果没有映射，尝试直接使用或转换为 PascalCase
  if (!iconKey) {
    // 将图标名称转换为 PascalCase（例如：home -> Home, menu-manage -> MenuManage）
    iconKey = iconName
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join('')
  }
  
  // 尝试查找对应的图标组件（按优先级尝试多种可能的名称）
  const IconComponent = ElementPlusIconsVue[iconKey] || 
                        ElementPlusIconsVue[iconName] ||
                        ElementPlusIconsVue[iconName.charAt(0).toUpperCase() + iconName.slice(1)] ||
                        ElementPlusIconsVue[iconName.toUpperCase()] ||
                        ElementPlusIconsVue[iconName.toLowerCase()]
  
  // 如果找到了图标组件，返回它
  if (IconComponent) {
    return IconComponent
  }
  
  // 如果没找到，尝试使用 Document 作为默认图标
  console.warn(`图标未找到: ${iconName}, 使用默认图标 Document`)
  return ElementPlusIconsVue['Document'] || null
}

// 更新标签页名称
const updateTabTitles = () => {
  tabs.value.forEach(tab => {
    if (!tab.fixed) {
      // 解析路径和查询参数
      let path = tab.path
      let query = {}
      if (path.includes('?')) {
        const [pathPart, queryPart] = path.split('?')
        path = pathPart
        queryPart.split('&').forEach(param => {
          const [key, value] = param.split('=')
          if (key && value) {
            query[key] = decodeURIComponent(value)
          }
        })
      }
      const newTitle = findMenuNameByPath(path, query)
      if (newTitle && newTitle !== '未知页面') {
        tab.title = newTitle
      }
    }
  })
}

// 判断是否是审批管理菜单
const isApprovalMenu = (menu) => {
  if (!menu) return false
  const menuPath = getMenuIndex(menu)
  const menuName = menu.menuName || ''
  const menuCode = menu.menuCode || ''
  
  // 精确匹配菜单名称（只匹配"审批管理"）
  if (menuName === '审批管理') {
    return true
  }
  
  // 检查路径（精确匹配审批管理相关路径）
  if (menuPath) {
    // 精确匹配审批管理的路径
    if (menuPath === '/home/manager-approval' || 
        menuPath === '/home/internal-approval' ||
        menuPath === '/home/approval-management' ||
        menuPath.includes('/manager/approval') ||
        menuPath.includes('/internal/approval')) {
      return true
    }
  }
  
  // 检查菜单代码（精确匹配）
  if (menuCode) {
    const codeLower = menuCode.toLowerCase()
    if (codeLower === 'approval' || 
        codeLower === 'approval-management' ||
        codeLower === 'manager-approval' ||
        codeLower === 'internal-approval') {
      return true
    }
  }
  
  return false
}

// 加载待审批数量（用于菜单角标）
// 使用和列表相同的查询逻辑，确保角标数字与列表一致
const loadApprovalPendingCount = async () => {
  // 只有内勤、会计、管理员才需要加载
  const userType = userInfo.value.userType
  if (!userType || 
      (userType !== 'ASSISTANT' && userType !== 'ACCOUNTANT' && userType !== 'MANAGER')) {
    return
  }
  
  try {
    // 使用和列表相同的查询接口，只查询待审批状态，不传递其他筛选条件
    const res = await getApprovalList({
      page: 1,
      pageSize: 1, // 只需要总数，不需要数据
      approvalStatus: 'PENDING' // 只查询待审批状态
    })
    if (res.code === '000000' && res.data) {
      // 取列表返回的 total 值作为角标数字
      approvalPendingCount.value = res.data.total || 0
    }
  } catch (error) {
    console.error('加载待审批数量失败:', error)
    // 失败时不影响其他功能，静默处理
  }
}

// 检查是否已显示过未读消息提醒（使用 localStorage 持久化）
const hasShownUnreadMessageAlert = () => {
  if (!userInfo.value.userId) {
    return false
  }
  const key = `unreadMessageAlert_${userInfo.value.userId}`
  return localStorage.getItem(key) === 'true'
}

// 标记已显示过未读消息提醒
const markUnreadMessageAlertShown = () => {
  if (!userInfo.value.userId) {
    return
  }
  const key = `unreadMessageAlert_${userInfo.value.userId}`
  localStorage.setItem(key, 'true')
}

// 加载未读消息数量（仅业务员）
const loadUnreadMessageCount = async (showAlert = false) => {
  // 只有业务员才需要加载未读消息数量
  if (userInfo.value.userType !== 'SALESMAN') {
    return
  }
  
  try {
    const res = await getUnreadMessageCount()
    if (res.code === '000000' && res.data !== undefined) {
      const previousCount = unreadMessageCount.value
      const newCount = Number(res.data) || 0
      unreadMessageCount.value = newCount
      
      // 如果是首次加载且有未读消息，显示提醒（只显示一次，使用 localStorage 持久化）
      if (showAlert && !hasShownUnreadMessageAlert() && newCount > 0) {
        markUnreadMessageAlertShown()
        ElMessage({
          message: `您有新的未读消息，请注意查收~ (右上角信封处)`,
          type: 'info',
          duration: 5000,
          showClose: true,
          customClass: 'unread-message-alert'
        })
      }
    }
  } catch (error) {
    console.error('加载未读消息数量失败:', error)
    // 失败时不影响其他功能，静默处理
  }
}

// 跳转到消息中心
const handleMessageCenterClick = () => {
  router.push('/home/message-center')
}

// 消息中心入口显示权限：优先使用当前内存中的菜单列表，避免依赖 localStorage 缓存
const canShowMessageCenter = computed(() => {
  // 优先根据当前加载的菜单树判断
  const menus = menuList.value || []
  const hasFromMenuList = (() => {
    const stack = [...menus]
    while (stack.length) {
      const m = stack.pop()
      if (!m) continue
      // 统一处理路径
      let path = m.path || ''
      if (path) {
        if (!path.startsWith('/home')) {
          path = path.startsWith('/') ? `/home${path}` : `/home/${path}`
        }
        if (path === '/home/message-center') {
          return true
        }
      }
      if (m.children && Array.isArray(m.children)) {
        stack.push(...m.children)
      }
    }
    return false
  })()

  if (hasFromMenuList) {
    return true
  }

  // 兜底：再用通用权限工具基于 localStorage 判断一次
  return hasRoutePermission('/home/message-center')
})

// 加载统计数据（仅系统管理员）
const loadStatistics = async () => {
  // 只有系统管理员才加载统计数据
  if (userInfo.value.userType !== 'SYSTEM_ADMIN') {
    return
  }

  try {
    // 1. 获取公司总数
    try {
      const companyRes = await getAllCompanies()
      if (companyRes?.code === '000000') {
        statsData.value.companyCount = companyRes.data?.length || 0
      }
    } catch (error) {
      console.error('获取公司总数失败:', error)
    }

    // 2. 获取企业用户总数（所有公司对应的启用状态的账号数）
    try {
      // 获取所有启用状态且有公司的用户
      const allCompanyUsersRes = await getUserList({ 
        pageNum: 1, 
        pageSize: 10000,
        status: 1
      })
      if (allCompanyUsersRes?.code === '000000') {
        const allUsers = allCompanyUsersRes.data?.records || []
        // 过滤出有公司ID的用户（企业用户）
        statsData.value.companyUserCount = allUsers.filter(user => user.companyId != null).length
      }
    } catch (error) {
      console.error('获取企业用户总数失败:', error)
    }

    // 3. 获取个人用户总数（没有所属企业的账号数）
    try {
      const personalUserRes = await getUserList({ 
        pageNum: 1, 
        pageSize: 10000,
        status: 1
      })
      if (personalUserRes?.code === '000000') {
        const allUsers = personalUserRes.data?.records || []
        // 过滤出没有公司ID的用户（个人用户）
        statsData.value.personalUserCount = allUsers.filter(user => user.companyId == null).length
      }
    } catch (error) {
      console.error('获取个人用户总数失败:', error)
    }

    // 4. 获取会员总数
    try {
      const membershipRes = await getMembershipList({ 
        pageNum: 1, 
        pageSize: 1
      })
      if (membershipRes?.code === '000000') {
        statsData.value.membershipCount = membershipRes.data?.total || 0
      }
    } catch (error) {
      console.error('获取会员总数失败:', error)
    }
  } catch (error) {
    console.error('加载统计数据失败:', error)
  }
}

// 加载管理员统计数据
const loadManagerStatistics = async () => {
  // 只有管理员才加载统计数据
  if (userInfo.value.userType !== 'MANAGER') {
    return
  }

  try {
    // 1. 获取客户总数
    try {
      const customerRes = await getManagerCustomerCount()
      if (customerRes?.code === '000000') {
        managerStatsData.value.customerCount = customerRes.data || 0
      }
    } catch (error) {
      console.error('获取客户总数失败:', error)
    }

    // 2. 获取待审批数量（使用和列表相同的查询逻辑）
    try {
      const approvalRes = await getApprovalList({
        page: 1,
        pageSize: 1,
        approvalStatus: 'PENDING'
      })
      if (approvalRes?.code === '000000' && approvalRes.data) {
        managerStatsData.value.pendingApprovalCount = approvalRes.data.total || 0
      }
    } catch (error) {
      console.error('获取待审批数量失败:', error)
    }

    // 3. 获取审批中数量（使用和列表相同的查询逻辑）
    try {
      const approvingRes = await getApprovalList({
        page: 1,
        pageSize: 1,
        approvalStatus: 'APPROVING'
      })
      if (approvingRes?.code === '000000' && approvingRes.data) {
        managerStatsData.value.approvingCount = approvingRes.data.total || 0
      }
    } catch (error) {
      console.error('获取审批中数量失败:', error)
    }

    // 4. 获取本月新增客户数
    try {
      const now = new Date()
      const startDate = new Date(now.getFullYear(), now.getMonth(), 1)
      const endDate = new Date(now)
      const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
      
      const dashboardRes = await getManagerDashboard({
        startDate: fmt(startDate),
        endDate: fmt(endDate)
      })
      if (dashboardRes?.code === '000000' && dashboardRes.data?.kpis) {
        managerStatsData.value.monthNewCustomer = dashboardRes.data.kpis.newCustomer || 0
      }
    } catch (error) {
      console.error('获取本月新增客户数失败:', error)
    }
  } catch (error) {
    console.error('加载管理员统计数据失败:', error)
  }
}

// 加载企业信息（仅管理员）
const loadCompanyInfo = async () => {
  // 只有管理员才加载企业信息
  if (userInfo.value.userType !== 'MANAGER') {
    return
  }

  try {
    const res = await getCurrentCompany()
    if (res?.code === '000000' && res.data) {
      companyInfo.value = {
        companyName: res.data.companyName || '',
        companyCode: res.data.companyCode || '',
        companyShortName: res.data.companyShortName || '',
        creditCode: res.data.creditCode || '',
        phone: res.data.phone || ''
      }
    }
  } catch (error) {
    console.error('加载企业信息失败:', error)
  }
}

// 加载业务员统计数据
const loadSalesmanStatistics = async () => {
  // 只有业务员才加载统计数据
  if (userInfo.value.userType !== 'SALESMAN') {
    return
  }

  try {
    // 1. 获取客户总数
    try {
      const customerRes = await getCustomerCount()
      if (customerRes?.code === '000000') {
        salesmanStatsData.value.customerCount = customerRes.data || 0
      }
    } catch (error) {
      console.error('获取客户总数失败:', error)
    }

    // 2. 获取合同总数
    try {
      const contractRes = await getContractCount()
      if (contractRes?.code === '000000') {
        salesmanStatsData.value.contractCount = contractRes.data || 0
      }
    } catch (error) {
      console.error('获取合同总数失败:', error)
    }

    // 3. 获取跟进统计（总跟进数）
    try {
      const followRes = await getFollowStatistics()
      if (followRes?.code === '000000') {
        const followStats = followRes.data || {}
        // 后端返回字段：totalCount / todayCount
        salesmanStatsData.value.followCount =
          typeof followStats.totalCount === 'number' ? followStats.totalCount : (followStats.totalCount || 0)
      }
    } catch (error) {
      console.error('获取跟进统计失败:', error)
    }

    // 4. 获取已通过的成交台账金额总和
    try {
      const dealRecordRes = await getDealRecordList({
        page: 1,
        pageSize: 10000, // 获取所有已通过的记录
        status: 'approved' // 只查询已通过的成交台账
      })
      if (dealRecordRes?.code === '000000' && dealRecordRes.data) {
        const dealRecordList = dealRecordRes.data.records || dealRecordRes.data.list || []
        // 计算已通过成交台账的金额总和
        const totalAmount = dealRecordList.reduce((sum, record) => {
          const amount = Number(record.dealAmount || 0)
          return sum + amount
        }, 0)
        salesmanStatsData.value.totalDealAmount = totalAmount
      }
    } catch (error) {
      console.error('获取成交金额统计失败:', error)
    }
  } catch (error) {
    console.error('加载业务员统计数据失败:', error)
  }
}

// 格式化金额
const formatMoney = (amount) => {
  if (!amount) return '0.00'
  return Number(amount).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

// 快捷操作
const handleQuickAction = (action) => {
  const routeMap = {
    customer: '/home/salesman-customer',
    contract: '/home/salesman-contract',
    follow: '/home/salesman-follow',
    report: '/home/salesman-report',
    travel: '/home/salesman-travel-expense',
    trip: '/home/salesman-business-trip',
    deal: '/home/salesman-deal-record',
    return: '/home/salesman-return-order'
  }
  
  const targetPath = routeMap[action]
  if (targetPath) {
    router.push(targetPath)
  }
}

// 系统管理员统计卡片点击处理
const handleSystemAdminStatClick = (type) => {
  if (type === 'company') {
    // 跳转到企业管理页面
    router.push('/home/company')
  } else if (type === 'companyUser') {
    // 跳转到用户管理页面，并筛选有公司的用户（企业用户）
    // 企业用户：有所属企业的用户（companyId != null）
    router.push({
      path: '/home/sys-user',
      query: {
        hasCompany: 'true',
        autoFilter: 'companyUser'
      }
    })
  } else if (type === 'personalUser') {
    // 跳转到用户管理页面，并筛选没有公司的用户（个人用户）
    // 个人用户：没有所属企业的用户（companyId == null）
    router.push({
      path: '/home/sys-user',
      query: {
        hasCompany: 'false',
        autoFilter: 'personalUser'
      }
    })
  } else if (type === 'membership') {
    // 跳转到会员管理页面
    router.push('/home/membership')
  }
}

// 管理员统计卡片点击处理
const handleManagerStatClick = (type) => {
  if (type === 'customer') {
    // 跳转到客户管理页面
    router.push('/home/manager-customer')
  } else if (type === 'monthNewCustomer') {
    // 跳转到客户管理页面，并设置本月日期范围筛选
    const now = new Date()
    const startDate = new Date(now.getFullYear(), now.getMonth(), 1)
    const endDate = new Date(now)
    const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    
    router.push({
      path: '/home/manager-customer',
      query: {
        createTimeStart: fmt(startDate),
        createTimeEnd: fmt(endDate),
        autoMonthFilter: 'true'
      }
    })
  } else if (type === 'pending') {
    // 跳转到审批管理页面，并设置状态筛选为待审批
    router.push({
      path: '/home/manager-approval',
      query: { 
        approvalStatus: 'PENDING',
        autoPending: 'true'
      }
    })
  } else if (type === 'approving') {
    // 跳转到审批管理页面，并设置状态筛选为审批中
    router.push({
      path: '/home/manager-approval',
      query: { 
        approvalStatus: 'APPROVING',
        autoApproving: 'true'
      }
    })
  }
}

// 菜单加载状态标志
let isMenuLoading = false

// 加载菜单数据
const loadMenus = async () => {
  // 如果正在加载，直接返回
  if (isMenuLoading) {
    console.log('菜单正在加载中，跳过重复加载')
    return
  }
  
  // 检查用户信息是否已设置
  if (!userInfo.value || !userInfo.value.userType) {
    console.warn('用户信息未设置，无法加载菜单')
    return
  }
  
  isMenuLoading = true
  try {
    console.log('开始加载菜单，用户类型:', userInfo.value.userType)
    // 所有用户（包括系统管理员）都使用 getUserMenuTree，根据角色和会员状态加载菜单
    const userType = userInfo.value.userType || ''
    const isMember = userInfo.value.isMember || 0
    console.log('用户类型:', userType, '是否会员:', isMember)
    const res = await getUserMenuTree(userType, isMember, 'PC')
    console.log('获取用户菜单树响应:', res)
    if (res && res.code === '000000') {
      menuList.value = res.data || []
      console.log('加载的菜单数据:', menuList.value)
      console.log('菜单数量:', menuList.value.length)
      
      // 调试：检查是否有仓库管理菜单
      const findWarehouseMenu = (menus) => {
        for (const menu of menus) {
          if (menu.menuName && (menu.menuName.includes('仓库') || menu.menuName.includes('warehouse'))) {
            return menu
          }
          if (menu.path && (menu.path.includes('warehouse') || menu.path.includes('仓库'))) {
            return menu
          }
          if (menu.menuCode && menu.menuCode.toLowerCase().includes('warehouse')) {
            return menu
          }
          if (menu.children && menu.children.length > 0) {
            const found = findWarehouseMenu(menu.children)
            if (found) return found
          }
        }
        return null
      }
      const warehouseMenu = findWarehouseMenu(menuList.value)
      if (warehouseMenu) {
        console.log('找到仓库管理菜单:', warehouseMenu)
      } else {
        console.warn('未找到仓库管理菜单！原始菜单数据:', menuList.value)
      }
      
      // 保存菜单列表到localStorage，供路由守卫使用
      try {
        localStorage.setItem('userMenuList', JSON.stringify(menuList.value))
      } catch (e) {
        console.warn('保存菜单列表到localStorage失败:', e)
      }
      
      // 菜单加载完成后，更新标签页名称
      updateTabTitles()
      
      // 调试：打印所有菜单项的 index
      const allMenuItems = getAllMenuPaths()
      console.log('所有菜单项路径:', allMenuItems.map(item => item.path))
      console.log('当前路由路径:', route.path)
      console.log('计算出的激活菜单:', activeMenu.value)
      
      // 菜单加载完成后，确保菜单选中状态和父菜单展开
      nextTick(() => {
        const targetIndex = activeMenu.value
        console.log('菜单加载完成，设置激活菜单:', targetIndex, '当前路由:', route.path)
        if (targetIndex) {
          // 更新激活菜单索引
          activeMenuIndex.value = targetIndex
          ensureParentMenuOpen(targetIndex)
          // 延迟一下确保 DOM 已更新（增加延迟时间，确保菜单项已渲染）
          setTimeout(() => {
            syncMenuActive()
          }, 300)
        }
      })
    } else {
      console.error('加载菜单失败:', res?.message || res?.code || '未知错误')
      menuList.value = []
      // 清除localStorage中的菜单列表
      try {
        localStorage.removeItem('userMenuList')
      } catch (e) {
        console.warn('清除菜单列表失败:', e)
      }
    }
  } catch (error) {
    console.error('加载菜单失败:', error)
    console.error('错误详情:', error.stack)
    menuList.value = []
    // 清除localStorage中的菜单列表
    try {
      localStorage.removeItem('userMenuList')
    } catch (e) {
      console.warn('清除菜单列表失败:', e)
    }
  } finally {
    isMenuLoading = false
  }
}

// 监听菜单更新事件
const handleMenuUpdate = async (event) => {
  console.log('收到菜单更新事件，重新加载菜单...', event)
  // 强制清空菜单列表，确保重新加载
  menuList.value = []
  // 等待一段时间，确保后端数据已更新
  await new Promise(resolve => setTimeout(resolve, 200))
  await loadMenus()
  console.log('菜单重新加载完成，当前菜单列表长度:', menuList.value.length)
  console.log('菜单数据:', menuList.value)
  // 更新标签页名称
  updateTabTitles()
}

// 监听 userInfo 变化，如果用户信息已设置但菜单未加载，则自动加载菜单
watch(
  () => userInfo.value.userType,
  async (newUserType) => {
    // 如果用户类型已设置，但菜单列表为空，且当前没有正在加载菜单，则加载菜单
    if (newUserType && (!menuList.value || menuList.value.length === 0) && !isMenuLoading) {
      console.log('检测到用户类型已设置但菜单未加载，自动加载菜单...', newUserType)
      await loadMenus()
    }
  },
  { immediate: true }
)

// 初始化缓存视图列表
const initCachedViews = () => {
  const routes = router.getRoutes()
  cachedViews.value = routes
    .filter(route => route.meta?.keepAlive && route.name)
    .map(route => route.name)
}

// 刷新缓存页面的数据（通过事件通知）
const refreshCachedPages = () => {
  // 发送全局事件，通知所有缓存的页面组件刷新数据
  window.dispatchEvent(new CustomEvent('page-cache-refresh', {
    detail: { timestamp: Date.now() }
  }))
}

onMounted(async () => {
  try {
    // 从localStorage获取用户信息
    const userInfoStr = localStorage.getItem('userInfo')
    if (userInfoStr) {
      try {
        const parsedUserInfo = JSON.parse(userInfoStr)
        // 确保 userInfo 被正确设置
        userInfo.value = parsedUserInfo
        console.log('用户信息已加载:', userInfo.value)
      } catch (parseError) {
        console.error('解析用户信息失败:', parseError)
        // 如果解析失败，清除无效数据并跳转到登录页
        localStorage.removeItem('userInfo')
        router.push('/login')
        return
      }
    } else {
      // 如果没有用户信息，跳转到登录页
      router.push('/login')
      return
    }
    
    // 初始化缓存视图列表
    initCachedViews()
    
    // 确保用户信息已设置后再加载菜单（使用 nextTick 确保响应式更新完成）
    await nextTick()
    
    // 如果用户类型为空，等待一下再重试（最多重试3次）
    let retryCount = 0
    const tryLoadMenus = async () => {
      if (!userInfo.value.userType && retryCount < 3) {
        retryCount++
        console.log(`用户类型为空，等待后重试 (${retryCount}/3)...`)
        await new Promise(resolve => setTimeout(resolve, 200))
        return tryLoadMenus()
      }
      // 加载菜单数据
      await loadMenus()
    }
    await tryLoadMenus()
    
    // 加载统计数据（仅系统管理员）
    loadStatistics()
    
    // 加载管理员统计数据
    loadManagerStatistics()
    
    // 加载业务员统计数据
    loadSalesmanStatistics()
    
    // 加载企业信息（仅管理员）
    loadCompanyInfo()
    
    // 加载待审批数量（用于菜单角标）
    loadApprovalPendingCount()
    
    // 定时刷新待审批数量（每30秒）
    approvalCountInterval = setInterval(() => {
      loadApprovalPendingCount()
    }, 30000)
    
    // 加载未读消息数量（仅业务员），首次加载时显示提醒
    if (userInfo.value.userType === 'SALESMAN') {
      // 延迟一下，确保页面渲染完成后再显示提醒
      setTimeout(() => {
        loadUnreadMessageCount(true)
      }, 500)
    }
    
    // 定时刷新未读消息数量（每5分钟，仅业务员）
    if (userInfo.value.userType === 'SALESMAN') {
      unreadMessageCountInterval = setInterval(() => {
        loadUnreadMessageCount(false)
      }, 300000)
    }
    
    // 定时刷新缓存页面数据（每2分钟）
    cacheRefreshInterval = setInterval(() => {
      refreshCachedPages()
    }, 120000) // 2分钟
    
    // 监听菜单更新事件
    window.addEventListener('menu-updated', handleMenuUpdate)
    
    // 监听关闭审批详情标签页的事件
    const handleCloseApprovalDetailTab = (event) => {
      const { path } = event.detail || {}
      if (path && path.startsWith('/home/approval-detail/')) {
        console.log('收到关闭审批详情标签页事件，路径:', path)
        handleTabClose(path)
      }
    }
    window.addEventListener('close-approval-detail-tab', handleCloseApprovalDetailTab)
    
    // 保存事件处理器引用，以便在卸载时移除
    window._closeApprovalDetailTabHandler = handleCloseApprovalDetailTab

    // 监听来自审批详情页的菜单激活事件（用于从详情返回列表时，确保菜单选中状态正确）
    const handleMenuSetActive = (event) => {
      const { path } = event.detail || {}
      if (path) {
        console.log('收到菜单激活事件，路径:', path)
        activeMenuIndex.value = path
        // 延迟同步一次菜单选中状态
        setTimeout(() => {
          syncMenuActive()
        }, 150)
      }
    }
    window.addEventListener('menu-set-active', handleMenuSetActive)
    window._menuSetActiveHandler = handleMenuSetActive
    
    // 监听刷新未读消息数量事件
    const handleRefreshUnreadCount = () => {
      if (userInfo.value.userType === 'SALESMAN') {
        // 刷新时不显示提醒，避免重复提醒
        loadUnreadMessageCount(false)
      }
    }
    window.addEventListener('refresh-unread-count', handleRefreshUnreadCount)
    
    // 保存事件处理器引用，以便在卸载时移除
    window._refreshUnreadCountHandler = handleRefreshUnreadCount
    
    // 监听审批待审批数量更新事件（从审批管理页面触发）
    const handleApprovalPendingCountUpdated = (event) => {
      const { count } = event.detail || {}
      if (typeof count === 'number') {
        console.log('收到审批待审批数量更新事件，更新角标数量:', count, '当前角标数量:', approvalPendingCount.value)
        // 更新角标数量，如果数量为0，角标会自动隐藏（因为 v-if="approvalPendingCount > 0"）
        approvalPendingCount.value = count
        console.log('角标数量已更新为:', approvalPendingCount.value)
      }
    }
    window.addEventListener('approval-pending-count-updated', handleApprovalPendingCountUpdated)
    
    // 保存事件处理器引用，以便在卸载时移除
    window._approvalPendingCountUpdatedHandler = handleApprovalPendingCountUpdated
    
    // 监听路由变化，动态更新缓存视图
    router.afterEach((to) => {
      if (to.meta?.keepAlive && to.name && !cachedViews.value.includes(to.name)) {
        cachedViews.value.push(to.name)
      }
    })
  } catch (error) {
    console.error('初始化失败:', error)
    ElMessage.error('页面初始化失败，请刷新重试')
  }
})

onUnmounted(() => {
  // 移除事件监听
  window.removeEventListener('menu-updated', handleMenuUpdate)
  if (window._closeApprovalDetailTabHandler) {
    window.removeEventListener('close-approval-detail-tab', window._closeApprovalDetailTabHandler)
    delete window._closeApprovalDetailTabHandler
  }
  if (window._menuSetActiveHandler) {
    window.removeEventListener('menu-set-active', window._menuSetActiveHandler)
    delete window._menuSetActiveHandler
  }
  if (window._refreshUnreadCountHandler) {
    window.removeEventListener('refresh-unread-count', window._refreshUnreadCountHandler)
    delete window._refreshUnreadCountHandler
  }
  if (window._approvalPendingCountUpdatedHandler) {
    window.removeEventListener('approval-pending-count-updated', window._approvalPendingCountUpdatedHandler)
    delete window._approvalPendingCountUpdatedHandler
  }
  // 清理定时器
  if (approvalCountInterval) {
    clearInterval(approvalCountInterval)
    approvalCountInterval = null
  }
  if (cacheRefreshInterval) {
    clearInterval(cacheRefreshInterval)
    cacheRefreshInterval = null
  }
  if (unreadMessageCountInterval) {
    clearInterval(unreadMessageCountInterval)
    unreadMessageCountInterval = null
  }
})

// 处理菜单点击（用于有子菜单的菜单项，点击时跳转并保持子菜单展开）
const handleMenuClick = (index) => {
  if (!index) return
  
  try {
    console.log('菜单点击（带子菜单）:', index)
    
    // 跳转到对应路由
    router.push(index).catch(err => {
      // 忽略导航重复的错误（用户快速点击时可能触发）
      if (err.name !== 'NavigationDuplicated') {
        console.error('路由跳转失败:', err)
      }
    })
    
    // 确保父菜单保持展开
    if (menuRef.value) {
      // 找到对应的 sub-menu index（格式为 menu-{id}）
      const findSubMenuIndex = (menus) => {
        for (const menu of menus) {
          if (menu.children) {
            for (const child of menu.children) {
              const childIndex = getMenuIndex(child)
              if (childIndex === index && child.children && child.children.length > 0) {
                return `menu-${child.id}`
              }
              // 递归查找
              if (child.children && child.children.length > 0) {
                const found = findSubMenuIndex([child])
                if (found) return found
              }
            }
          }
        }
        return null
      }
      
      const subMenuIndex = findSubMenuIndex(menuList.value)
      if (subMenuIndex) {
        // 确保子菜单展开
        nextTick(() => {
          try {
            if (menuRef.value) {
              // 如果子菜单未展开，则展开它
              if (!menuRef.value.openedMenus || !menuRef.value.openedMenus.includes(subMenuIndex)) {
                menuRef.value.open(subMenuIndex)
              }
            }
          } catch (err) {
            // 忽略菜单操作错误（可能是 ResizeObserver 相关）
            if (!String(err).toLowerCase().includes('resizeobserver')) {
              console.error('菜单展开失败:', err)
            }
          }
        })
      }
    }
  } catch (err) {
    // 忽略 ResizeObserver 相关错误
    if (!String(err).toLowerCase().includes('resizeobserver')) {
      console.error('菜单点击处理失败:', err)
    }
  }
}

const handleMenuSelect = (index) => {
  console.log('菜单选择:', index, '当前路由:', route.path)

  // 更新激活菜单索引
  activeMenuIndex.value = index

  if (menuRef.value && index) {
    try {
      // 确保组件实例的 activeIndex 正确设置
      if (menuRef.value.activeIndex !== undefined) {
        menuRef.value.activeIndex = index
      }
      console.log('设置菜单激活状态:', index)
    } catch (error) {
      console.warn('设置菜单激活状态失败:', error)
    }
  }
  
  // 如果是审批管理页面，且角标有数字，传递参数强制设置为待审批状态
  // 检查路径是否包含审批管理相关的路径
  const isApprovalPage = index && (
    index.includes('/manager-approval') || 
    index.includes('/internal-approval') || 
    index.includes('/approval-management') ||
    index === '/home/manager-approval' ||
    index === '/home/internal-approval'
  )
  
  if (isApprovalPage && approvalPendingCount.value > 0) {
    // 有角标数字，传递参数标识从角标点击进入
    console.log('从角标点击审批管理，传递参数强制设置为待审批状态')
    router.push({
      path: index,
      query: { fromBadge: 'true', autoPending: 'true' }
    })
    return
  }
  
  router.push(index)
}

// 切换侧边栏收起/展开
const toggleSidebar = () => {
  isCollapse.value = !isCollapse.value
}

// 同步 el-menu 的激活项
const syncMenuActive = () => {
  const targetIndex = activeMenu.value
  if (!targetIndex || !menuRef.value) {
    console.warn('syncMenuActive: targetIndex 或 menuRef 为空', { targetIndex, menuRef: !!menuRef.value })
    return
  }
  
  console.log('同步菜单激活状态:', targetIndex, '当前路由:', route.path)
  
  // Element Plus 的 el-menu 组件在使用 :router="true" 时，会自动根据路由匹配
  // 但 default-active 只在初始化时生效，所以我们需要通过组件实例来更新
  nextTick(() => {
    try {
      // 方法1: 直接设置 activeIndex（如果组件支持）
      if (menuRef.value && menuRef.value.activeIndex !== undefined) {
        menuRef.value.activeIndex = targetIndex
        console.log('设置 activeIndex:', targetIndex)
      }
      
      // 方法2: 通过调用组件的方法来设置（如果存在）
      if (menuRef.value && typeof menuRef.value.setActiveIndex === 'function') {
        menuRef.value.setActiveIndex(targetIndex)
        console.log('调用 setActiveIndex:', targetIndex)
      }
      
      // 方法3: 通过直接操作 DOM 来确保目标菜单项激活（只操作目标项，不干扰其他项）
      // Element Plus 会自动添加 is-active 类，我们只确保目标项有激活状态
      setTimeout(() => {
        try {
          const menuItems = document.querySelectorAll('.sidebar-menu .el-menu-item')
          
          // 输出所有菜单项的详细信息用于调试（仅对仓库管理）
          if (targetIndex.includes('warehouse')) {
            const allMenuInfo = Array.from(menuItems).map((item, idx) => {
              const index = item.getAttribute('index')
              const text = item.textContent?.trim() || ''
              const href = item.querySelector('a')?.getAttribute('href') || ''
              return { idx, index, text, href }
            })
            console.log('syncMenuActive - 所有菜单项详细信息:', allMenuInfo)
            // 单独输出每个菜单项
            allMenuInfo.forEach(item => {
              console.log(`菜单项 ${item.idx}: index="${item.index}", text="${item.text}", href="${item.href}"`)
            })
            
            // 也输出所有 sub-menu 的 title
            const subMenus = document.querySelectorAll('.sidebar-menu .el-sub-menu')
            console.log('所有子菜单数量:', subMenus.length)
            subMenus.forEach((subMenu, idx) => {
              const subIndex = subMenu.getAttribute('index')
              const title = subMenu.querySelector('.el-sub-menu__title')?.textContent?.trim() || ''
              console.log(`子菜单 ${idx}: index="${subIndex}", title="${title}"`)
            })
          }
          
          // 尝试多种方式查找目标菜单项
          let targetItem = null
          
          // 方式1: 通过 index 属性精确匹配（普通菜单项）
          targetItem = Array.from(menuItems).find(item => {
            const itemIndex = item.getAttribute('index')
            return itemIndex === targetIndex
          })
          
          // 方式2: 如果是仓库管理，查找对应的 sub-menu（因为仓库管理有子菜单，被渲染为 sub-menu）
          if (!targetItem && targetIndex.includes('warehouse')) {
            const subMenus = document.querySelectorAll('.sidebar-menu .el-sub-menu')
            const warehouseSubMenu = Array.from(subMenus).find(subMenu => {
              const title = subMenu.querySelector('.el-sub-menu__title')?.textContent?.trim() || ''
              return title.includes('仓库') || title.toLowerCase().includes('warehouse')
            })
            
            if (warehouseSubMenu) {
              // 找到对应的 sub-menu，激活它的 title 部分
              const titleElement = warehouseSubMenu.querySelector('.el-sub-menu__title')
              if (titleElement) {
                // 移除其他子菜单的激活状态
                subMenus.forEach(subMenu => {
                  const title = subMenu.querySelector('.el-sub-menu__title')
                  if (title) {
                    title.classList.remove('is-active')
                    // 移除内联样式
                    title.style.backgroundColor = ''
                    title.style.borderRight = ''
                    title.style.color = ''
                  }
                })
                // 激活目标子菜单的 title
                titleElement.classList.add('is-active')
                // 使用内联样式确保样式生效（优先级最高）
                titleElement.style.backgroundColor = '#ecf5ff'
                titleElement.style.borderRight = '3px solid #409eff'
                titleElement.style.color = '#409eff'
                titleElement.style.fontWeight = '600'
                const subIndex = warehouseSubMenu.getAttribute('index')
                console.log('syncMenuActive - 成功激活子菜单标题:', subIndex, 'title:', titleElement.textContent?.trim())
                
                // 同时展开子菜单（即使 index 是 null，也尝试通过其他方式展开）
                if (menuRef.value) {
                  try {
                    // 尝试通过 index 展开
                    if (subIndex) {
                      menuRef.value.open(subIndex)
                    } else {
                      // 如果 index 是 null，尝试通过点击 title 来展开
                      const clickEvent = new MouseEvent('click', { bubbles: true, cancelable: true })
                      titleElement.dispatchEvent(clickEvent)
                    }
                  } catch (e) {
                    console.warn('展开子菜单失败:', e)
                  }
                }
              }
              // 注意：sub-menu 的 title 不是 menu-item，所以不需要继续查找 menu-item
              return
            }
          }
          
          // 方式3: 通过 href 属性匹配（Element Plus 使用 router 时会设置 href）
          if (!targetItem) {
            targetItem = Array.from(menuItems).find(item => {
              const link = item.querySelector('a')
              if (link) {
                const href = link.getAttribute('href')
                return href === targetIndex || href === targetIndex.replace('/home', '')
              }
              return false
            })
          }
          
          // 方式4: 如果精确匹配失败，尝试匹配仓库管理的各种路径格式
          if (!targetItem && targetIndex.includes('warehouse')) {
            targetItem = Array.from(menuItems).find(item => {
              const itemIndex = item.getAttribute('index')
              const link = item.querySelector('a')
              const href = link?.getAttribute('href') || ''
              return itemIndex === '/home/internal-warehouse' ||
                     itemIndex === '/home/assistant/warehouse' ||
                     itemIndex === '/home/internal/warehouse' ||
                     (itemIndex && itemIndex.includes('warehouse')) ||
                     href.includes('warehouse')
            })
          }
          
          // 方式5: 通过菜单文本内容匹配（作为最后的手段）
          if (!targetItem && targetIndex.includes('warehouse')) {
            targetItem = Array.from(menuItems).find(item => {
              const text = item.textContent?.trim() || ''
              return text.includes('仓库') || text.toLowerCase().includes('warehouse')
            })
          }
          
          if (targetItem) {
            // 确保目标项有激活状态
            targetItem.classList.add('is-active', 'router-link-active', 'router-link-exact-active')
            const actualIndex = targetItem.getAttribute('index')
            const text = targetItem.textContent?.trim() || ''
            console.log('syncMenuActive - 成功激活菜单项:', { actualIndex, text, target: targetIndex })
          } else {
            console.warn('syncMenuActive - 未找到匹配的菜单项，target:', targetIndex)
          }
        } catch (error) {
          console.warn('DOM 操作失败:', error)
        }
      }, 100)
    } catch (error) {
      console.warn('同步菜单激活状态失败:', error)
    }
  })
}

// 处理子菜单展开，确保选中菜单的父菜单自动展开
const handleSubMenuOpen = (index) => {
  // Element Plus 会自动处理，这里可以添加额外逻辑
  console.log('子菜单展开:', index)
}

// 确保父菜单展开（当子菜单项被选中时），同时如果目标菜单本身有子菜单也展开
const ensureParentMenuOpen = (menuPath) => {
  if (!menuRef.value || !menuPath) return
  
  // 查找菜单路径对应的父菜单索引，以及目标菜单本身是否有子菜单
  let targetMenuHasChildren = false
  let targetSubMenuIndex = null
  
  const findParentMenuIndexes = (menus, targetPath, parentIndexes = []) => {
    for (const menu of menus) {
      const menuPath = getMenuIndex(menu)
      
      // 如果是目录类型，使用目录索引
      if (menu.menuType === 'DIRECTORY') {
        const dirIndex = `dir-${menu.id}`
        if (menu.children && menu.children.length > 0) {
          const found = findParentMenuIndexes(menu.children, targetPath, [...parentIndexes, dirIndex])
          if (found) return found
        }
      } else if (menuPath === targetPath) {
        // 找到目标菜单，检查是否有子菜单
        if (menu.menuType === 'MENU' && menu.children && menu.children.length > 0) {
          targetMenuHasChildren = true
          targetSubMenuIndex = `menu-${menu.id}`
        }
        // 返回父菜单索引列表
        return parentIndexes
      } else if (menu.children && menu.children.length > 0) {
        // 继续在子菜单中查找
        const menuIndex = menu.menuType === 'MENU' && menu.children.length > 0 ? `menu-${menu.id}` : null
        const newParentIndexes = menuIndex ? [...parentIndexes, menuIndex] : parentIndexes
        const found = findParentMenuIndexes(menu.children, targetPath, newParentIndexes)
        if (found) return found
      }
    }
    return null
  }
  
  const parentIndexes = findParentMenuIndexes(menuList.value, menuPath)
  if (menuRef.value) {
    // 展开所有父菜单
    nextTick(() => {
      if (parentIndexes && parentIndexes.length > 0) {
        parentIndexes.forEach(index => {
          if (menuRef.value && !menuRef.value.openedMenus?.includes(index)) {
            menuRef.value.open(index)
          }
        })
      }
      // 如果目标菜单本身有子菜单，也展开它
      if (targetMenuHasChildren && targetSubMenuIndex) {
        if (menuRef.value && !menuRef.value.openedMenus?.includes(targetSubMenuIndex)) {
          menuRef.value.open(targetSubMenuIndex)
        }
      }
    })
  }
}

// 路径到中文名称的映射
const pathNameMap = {
  '/home': '首页',
  '/home/sys-user': '用户管理',
  '/home/sys-menu': '菜单管理',
  '/home/sys-role': '角色管理',
  '/home/sys-permission': '权限管理',
  '/home/company': '企业管理',
  '/home/operation-log': '日志管理',
  '/home/scheduled-task': '定时任务管理',
  '/home/membership': '会员管理',
  '/home/approval-flow-config': '审批流配置管理',
  '/home/workflow': '审批流配置管理',
  '/home/member': '会员管理',
  '/home/manager-user': '账号管理',
  '/home/manager-company': '公司管理',
  '/home/manager-report': '数据报表',
  '/home/manager-customer': '客户管理',
  '/home/manager/company': '公司管理',
  '/home/manager/report': '数据报表',
  '/home/manager/customer': '客户管理',
  '/home/manager/user': '账号管理',
  '/home/manager-account': '账号管理',
  // 业务员返货&成交模块（兼容旧路径）
  '/home/salesman-return-order': '返货管理',
  '/home/salesman-return': '返货管理',
  '/home/salesman-deal-record': '成交台账',
  '/home/salesman-deal': '成交台账',
  '/home/internal-approval': '审批管理',
  '/home/manager-approval': '审批管理',
  '/home/approval-management': '审批管理',
  '/home/message-center': '消息中心',
  // 业务员出差模块
  '/home/salesman-trip': '出差申请',
  '/home/salesman-business-trip': '出差申请',
  // 内勤模块（兼容 assistant 路径格式）
  '/home/internal-warehouse': '仓库管理',
  '/home/assistant/warehouse': '仓库管理',
  '/home/internal-product': '商品管理',
  '/home/assistant/product': '商品管理',
  '/home/salesman-product': '商品管理',
  '/home/internal-delivery': '发货单管理',
  '/home/assistant/delivery': '发货单管理',
  '/home/internal-purchase': '采购管理',
  '/home/assistant/purchase': '采购管理'
}

// 根据路径查找菜单名称
const findMenuNameByPath = (path, query = {}) => {
  // 如果是首页
  if (path === '/home' || path === '/') {
    return '首页'
  }
  
  // 消息中心分类明细页：优先使用路由query中的标题
  if (path === '/home/message-center/detail') {
    if (query && query.title) {
      return String(query.title)
    }
    return '消息明细'
  }
  
  // 处理动态路由（如审批详情页）
  if (path.startsWith('/home/approval-detail/')) {
    return '审批详情'
  }
  
  // 处理合同表单页面（新增/编辑）
  if (path === '/home/salesman-contract-form') {
    // 根据查询参数判断是新增还是编辑
    if (query && query.id) {
      return '编辑合同'
    }
    return '新增合同'
  }
  
  // 处理返货单表单页面（新增/编辑）
  if (path === '/home/salesman-return-order-form') {
    // 根据查询参数判断是新增还是编辑
    if (query && query.id) {
      return '编辑返货单'
    }
    return '新增返货单'
  }
  
  // 先检查映射表
  if (pathNameMap[path]) {
    return pathNameMap[path]
  }
  
  // 递归查找菜单
  const findInMenuList = (menus, targetPath) => {
    if (!menus || !Array.isArray(menus)) {
      return null
    }
    for (const menu of menus) {
      if (!menu) continue
      // 获取菜单路径（使用统一的路径获取方法）
      const menuPath = getMenuIndex(menu)
      if (!menuPath) continue
      
      // 精确匹配
      if (menuPath === targetPath && menu.menuType === 'MENU') {
        return menu.menuName
      }
      
      // 路径匹配（处理转换后的路径）
      // 例如：targetPath 是 /home/manager-company，menuPath 可能是 /home/manager/company
      const normalizedTargetPath = targetPath.replace(/\/(manager|salesman|internal)-/g, '/$1/')
      const normalizedMenuPath = menuPath.replace(/\/(manager|salesman|internal)-/g, '/$1/')
      if (normalizedTargetPath === normalizedMenuPath && menu.menuType === 'MENU') {
        return menu.menuName
      }
      
      // 检查子菜单
      if (menu.children && Array.isArray(menu.children) && menu.children.length > 0) {
        const found = findInMenuList(menu.children, targetPath)
        if (found) return found
      }
    }
    return null
  }
  
  const menuName = findInMenuList(menuList.value, path)
  if (menuName) {
    return menuName
  }
  
  // 如果找不到，返回未知页面
  return '未知页面'
}

// 添加标签页
const addTab = (path, query = {}) => {
  // 如果是首页，直接返回（首页已固定）
  if (path === '/home' || path === '/') {
    return
  }
  
  // 对于合同表单页面和返货单表单页面，需要根据查询参数生成唯一的路径标识
  let tabKey = path
  if (path === '/home/salesman-contract-form') {
    // 合同表单页面，如果有id，使用带id的路径作为key，否则使用基础路径
    tabKey = query && query.id ? `${path}?id=${query.id}` : path
  } else if (path === '/home/salesman-return-order-form') {
    // 返货单表单页面，如果有id，使用带id的路径作为key，否则使用基础路径
    tabKey = query && query.id ? `${path}?id=${query.id}` : path
  }
  
  // 检查标签是否已存在（使用tabKey进行比较）
  const existingTab = tabs.value.find(tab => {
    if (tab.path === tabKey) return true
    // 兼容处理：如果路径相同，也认为是同一个标签
    if (tab.path === path && path !== '/home/salesman-contract-form' && path !== '/home/salesman-return-order-form') return true
    return false
  })
  if (existingTab) {
    // 如果已存在，更新标题（因为新增/编辑可能切换）
    if (path === '/home/salesman-contract-form') {
      const newTitle = findMenuNameByPath(path, query)
      if (existingTab.title !== newTitle) {
        existingTab.title = newTitle
      }
      // 更新路径key
      existingTab.path = tabKey
    }
    return
  }
  
  // 添加新标签
  const title = findMenuNameByPath(path, query)
  
  // 特殊处理：避免"审批管理"出现多个标签（内勤/管理员返回时路径不同但标题相同）
  // 业务上同一账号不会同时需要两个审批列表标签，保留第一个即可
  if (title === '审批管理') {
    const existsSameTitle = tabs.value.find(tab => tab.title === '审批管理')
    if (existsSameTitle) {
      // 即使标签已存在，也要更新现有标签的路径（可能从详情页返回时路径略有不同）
      if (existsSameTitle.path !== tabKey && existsSameTitle.path !== path) {
        existsSameTitle.path = tabKey
      }
      // 返回 false 表示标签已存在，但需要继续处理菜单状态
      return false
    }
  }

  tabs.value.push({
    path: tabKey,
    title,
    fixed: false
  })
  return true
}

// 关闭标签页
const handleTabClose = (path) => {
  // 首页不能关闭
  if (path === '/home' || path === '/') {
    return
  }

  // 优先精确匹配路径
  let index = tabs.value.findIndex(tab => tab.path === path)

  // 如果精确匹配不到，并且是审批详情页，尝试按前缀匹配（兼容带查询参数或id变化的情况）
  if (index === -1 && path.startsWith('/home/approval-detail/')) {
    index = tabs.value.findIndex(tab => tab.path.startsWith('/home/approval-detail/'))
  }

  if (index === -1) return
  
  // 如果关闭的是仓库管理相关的标签，移除子菜单标题的激活状态
  if (path.includes('warehouse') || path === '/home/internal-warehouse' || path === '/home/assistant/warehouse') {
    nextTick(() => {
      const subMenus = document.querySelectorAll('.sidebar-menu .el-sub-menu')
      subMenus.forEach(subMenu => {
        const title = subMenu.querySelector('.el-sub-menu__title')
        if (title) {
          const titleText = title.textContent?.trim() || ''
          if (titleText.includes('仓库') || titleText.toLowerCase().includes('warehouse')) {
            title.classList.remove('is-active')
            // 移除内联样式
            title.style.backgroundColor = ''
            title.style.borderRight = ''
            title.style.color = ''
            title.style.fontWeight = ''
          }
        }
      })
    })
  }
  
  // 如果关闭的是当前激活的标签，需要切换到其他标签
  if (activeTab.value === tabs.value[index].path) {
    // 优先切换到右侧的标签，如果没有则切换到左侧
    if (index < tabs.value.length - 1) {
      const nextTab = tabs.value[index + 1]
      // 使用 handleTabClick 来正确处理带查询参数的路径
      handleTabClick(nextTab.path)
    } else if (index > 0) {
      const prevTab = tabs.value[index - 1]
      // 使用 handleTabClick 来正确处理带查询参数的路径
      handleTabClick(prevTab.path)
    } else {
      // 如果没有其他标签，切换到首页
      router.push('/home')
    }
  }
  
  // 移除标签
  tabs.value.splice(index, 1)
}

// 点击标签切换路由
const handleTabClick = (path) => {
  // 如果路径包含查询参数（如合同表单页面）
  if (path.includes('?')) {
    const [pathPart, queryPart] = path.split('?')
    const query = {}
    queryPart.split('&').forEach(param => {
      const [key, value] = param.split('=')
      if (key && value) {
        query[key] = decodeURIComponent(value)
      }
    })
    router.push({ path: pathPart, query })
  } else {
    router.push(path)
  }
}

// 提供关闭标签页的方法给子组件使用
provide('closeTab', handleTabClose)

// 右键菜单相关
const contextMenuVisible = ref(false)
const contextMenuX = ref(0)
const contextMenuY = ref(0)
const contextMenuTargetPath = ref('')

// 保存标签右键菜单的点击事件处理器引用，避免内存泄漏
let tabContextMenuCloseHandler = null

// 处理标签右键菜单
const handleTabContextMenu = (event, path) => {
  // 首页不能右键关闭
  if (path === '/home' || path === '/') {
    return
  }
  
  contextMenuTargetPath.value = path
  contextMenuX.value = event.clientX
  contextMenuY.value = event.clientY
  contextMenuVisible.value = true
  
  // 先移除之前的监听器（如果存在），避免重复添加
  if (tabContextMenuCloseHandler) {
    document.removeEventListener('click', tabContextMenuCloseHandler)
    tabContextMenuCloseHandler = null
  }
  
  // 点击其他地方关闭菜单
  tabContextMenuCloseHandler = () => {
    contextMenuVisible.value = false
    if (tabContextMenuCloseHandler) {
      document.removeEventListener('click', tabContextMenuCloseHandler)
      tabContextMenuCloseHandler = null
    }
  }
  nextTick(() => {
    if (tabContextMenuCloseHandler) {
      document.addEventListener('click', tabContextMenuCloseHandler)
    }
  })
}

// 关闭左侧标签
const handleCloseLeft = () => {
  const targetIndex = tabs.value.findIndex(tab => tab.path === contextMenuTargetPath.value)
  if (targetIndex === -1) {
    contextMenuVisible.value = false
    return
  }
  
  // 找到目标标签左侧的所有非固定标签路径（先收集路径，避免索引变化）
  const pathsToClose = []
  for (let i = 0; i < targetIndex; i++) {
    if (!tabs.value[i].fixed) {
      pathsToClose.push(tabs.value[i].path)
    }
  }
  
  if (pathsToClose.length === 0) {
    contextMenuVisible.value = false
    return
  }
  
  // 检查当前激活的标签是否在要关闭的列表中
  const currentActivePath = activeTab.value
  const needSwitch = pathsToClose.includes(currentActivePath)
  
  // 批量删除：使用filter过滤掉要关闭的标签
  tabs.value = tabs.value.filter(tab => !pathsToClose.includes(tab.path))
  
  // 如果关闭了当前激活的标签，需要切换到目标标签
  if (needSwitch) {
    nextTick(() => {
      handleTabClick(contextMenuTargetPath.value)
    })
  }
  
  contextMenuVisible.value = false
}

// 关闭右侧标签
const handleCloseRight = () => {
  const targetIndex = tabs.value.findIndex(tab => tab.path === contextMenuTargetPath.value)
  if (targetIndex === -1) {
    contextMenuVisible.value = false
    return
  }
  
  // 找到目标标签右侧的所有非固定标签路径（先收集路径，避免索引变化）
  const pathsToClose = []
  for (let i = targetIndex + 1; i < tabs.value.length; i++) {
    if (!tabs.value[i].fixed) {
      pathsToClose.push(tabs.value[i].path)
    }
  }
  
  if (pathsToClose.length === 0) {
    contextMenuVisible.value = false
    return
  }
  
  // 检查当前激活的标签是否在要关闭的列表中
  const currentActivePath = activeTab.value
  const needSwitch = pathsToClose.includes(currentActivePath)
  
  // 批量删除：使用filter过滤掉要关闭的标签
  tabs.value = tabs.value.filter(tab => !pathsToClose.includes(tab.path))
  
  // 如果关闭了当前激活的标签，需要切换到目标标签
  if (needSwitch) {
    nextTick(() => {
      handleTabClick(contextMenuTargetPath.value)
    })
  }
  
  contextMenuVisible.value = false
}

// 关闭其他标签
const handleCloseOthers = () => {
  const targetPath = contextMenuTargetPath.value
  const targetTab = tabs.value.find(tab => tab.path === targetPath)
  
  if (!targetTab) {
    contextMenuVisible.value = false
    return
  }
  
  // 找到除了目标标签之外的所有非固定标签路径
  const pathsToClose = tabs.value
    .filter(tab => tab.path !== targetPath && !tab.fixed)
    .map(tab => tab.path)
  
  if (pathsToClose.length === 0) {
    contextMenuVisible.value = false
    return
  }
  
  // 检查当前激活的标签是否在要关闭的列表中
  const currentActivePath = activeTab.value
  const needSwitch = pathsToClose.includes(currentActivePath)
  
  // 批量删除：使用filter过滤掉要关闭的标签，保留目标标签和固定标签
  tabs.value = tabs.value.filter(tab => 
    tab.path === targetPath || tab.fixed || !pathsToClose.includes(tab.path)
  )
  
  // 如果关闭了当前激活的标签，需要切换到目标标签
  if (needSwitch) {
    nextTick(() => {
      handleTabClick(targetPath)
    })
  }
  
  contextMenuVisible.value = false
}

// 全部关闭
const handleCloseAll = () => {
  // 找到所有非固定标签的路径（先收集路径，避免索引变化）
  const pathsToClose = tabs.value
    .filter(tab => !tab.fixed)
    .map(tab => tab.path)
  
  if (pathsToClose.length === 0) {
    contextMenuVisible.value = false
    return
  }
  
  // 检查当前激活的标签是否在要关闭的列表中
  const currentActivePath = activeTab.value
  const needSwitch = pathsToClose.includes(currentActivePath)
  
  // 批量删除：使用filter过滤掉所有非固定标签
  tabs.value = tabs.value.filter(tab => tab.fixed)
  
  // 如果关闭了当前激活的标签，切换到首页
  if (needSwitch) {
    nextTick(() => {
      router.push('/home')
    })
  }
  
  contextMenuVisible.value = false
}

// 监听路由变化，自动添加标签并更新菜单选中状态
watch(
  () => ({ path: route.path, query: route.query }),
  (newRoute, oldRoute) => {
    const newPath = newRoute.path
    const oldPath = oldRoute?.path
    console.log('路由变化:', newPath, '从:', oldPath, '查询参数:', newRoute.query)
    
    // 如果从审批详情页面离开，关闭审批详情标签页（兼容不同路径形式）
    if (oldPath && oldPath.startsWith('/home/approval-detail/')) {
      // 优先精确匹配
      let index = tabs.value.findIndex(tab => tab.path === oldPath)
      // 如果精确匹配不到，按前缀匹配（兼容带查询参数或id变化的情况）
      if (index === -1) {
        index = tabs.value.findIndex(tab => tab.path.startsWith('/home/approval-detail/'))
      }
      if (index !== -1) {
        console.log('检测到从审批详情页面离开，关闭标签页:', tabs.value[index].path)
        tabs.value.splice(index, 1)
      }
      // 确保所有审批详情相关的标签都被关闭（防止有多个）
      tabs.value = tabs.value.filter(tab => !tab.path.startsWith('/home/approval-detail/'))
      
      // 重要：从审批详情返回列表页时，确保菜单状态正确更新
      // 因为 activeMenu computed 依赖于 tabs.value，需要等待标签更新后再更新菜单状态
      nextTick(() => {
        const targetIndex = activeMenu.value
        if (targetIndex) {
          console.log('从审批详情返回，强制更新菜单选中状态:', targetIndex)
          ensureParentMenuOpen(targetIndex)
          activeMenuIndex.value = targetIndex
          setTimeout(() => {
            syncMenuActive()
          }, 200)
        }
      })
    }
    
    // 如果是首页，确保首页标签存在
    if (newPath === '/home' || newPath === '/') {
      const homeTab = tabs.value.find(tab => tab.path === '/home')
      if (!homeTab) {
        tabs.value.unshift({
          path: '/home',
          title: '首页',
          fixed: true
        })
      }
      return
    }
    
    // 其他路径，添加标签（传递查询参数）
    const tabAdded = addTab(newPath, newRoute.query)
    
    // 无论标签是否已存在，都要确保菜单选中状态正确更新
    // 使用 nextTick 确保标签添加完成后再更新菜单状态（特别是从审批详情返回列表页时）
    nextTick(() => {
      const targetIndex = activeMenu.value
      if (targetIndex) {
        ensureParentMenuOpen(targetIndex)
        console.log('路由变化后更新菜单选中状态:', targetIndex, '标签是否新增:', tabAdded, '当前路由:', newPath)
        // 更新激活菜单索引
        activeMenuIndex.value = targetIndex
        // 延迟一下确保 DOM 已更新（增加延迟时间，确保菜单项已渲染）
        setTimeout(() => {
          syncMenuActive()
        }, 200)
      } else {
        console.warn('未找到匹配的菜单项，当前路由:', newPath)
      }
    })
  },
  { immediate: true, deep: true }
)

// 监听 activeMenu 变化，更新菜单选中状态并展开父菜单
watch(
  () => activeMenu.value,
  (newActiveMenu) => {
    if (!newActiveMenu) return
    
    console.log('activeMenu 变化:', newActiveMenu, '当前路由:', route.path)
    
    // 更新激活菜单索引
    activeMenuIndex.value = newActiveMenu
    
    // 确保父菜单展开
    ensureParentMenuOpen(newActiveMenu)

    // 同步激活项（延迟一下确保 DOM 已更新）
    nextTick(() => {
      if (menuRef.value && newActiveMenu) {
        // Element Plus 的菜单组件在使用 :router="true" 时，会自动根据路由匹配
        // 但我们需要确保组件实例的 activeIndex 正确设置
        try {
          // 直接设置 activeIndex
          if (menuRef.value.activeIndex !== undefined) {
            menuRef.value.activeIndex = newActiveMenu
          }
          
          // 如果组件有 setActiveIndex 方法，调用它
          if (typeof menuRef.value.setActiveIndex === 'function') {
            menuRef.value.setActiveIndex(newActiveMenu)
          }
          
          // 调试：打印组件实例的属性
          console.log('菜单组件实例:', menuRef.value)
          console.log('activeIndex:', menuRef.value.activeIndex, 'target:', newActiveMenu)
          
          // 延迟一下，确保 Element Plus 已经处理完路由匹配
          // Element Plus 的菜单组件在使用 :router="true" 时会自动处理激活状态
          // 我们只需要在必要时补充激活状态，不要干扰自动机制
          setTimeout(() => {
            const menuItems = document.querySelectorAll('.sidebar-menu .el-menu-item')
            
            // 输出所有菜单项的 index 用于调试
            const allIndexes = Array.from(menuItems).map(item => ({
              index: item.getAttribute('index'),
              text: item.textContent?.trim() || ''
            }))
            console.log('所有菜单项 index:', allIndexes)
            // 单独输出每个菜单项的 index 值，方便查看
            allIndexes.forEach((item, idx) => {
              console.log(`菜单项 ${idx}: index="${item.index}", text="${item.text}"`)
            })
            
            // 尝试多种方式查找目标菜单项
            let targetItem = null
            
            // 方式1: 精确匹配
            targetItem = Array.from(menuItems).find(item => {
              const itemIndex = item.getAttribute('index')
              return itemIndex === newActiveMenu
            })
            
            // 方式2: 如果是仓库管理，查找对应的 sub-menu（因为仓库管理有子菜单，被渲染为 sub-menu）
            if (!targetItem && newActiveMenu.includes('warehouse')) {
              const subMenus = document.querySelectorAll('.sidebar-menu .el-sub-menu')
              const warehouseSubMenu = Array.from(subMenus).find(subMenu => {
                const title = subMenu.querySelector('.el-sub-menu__title')?.textContent?.trim() || ''
                return title.includes('仓库') || title.toLowerCase().includes('warehouse')
              })
              
              if (warehouseSubMenu) {
                // 找到对应的 sub-menu，激活它的 title 部分
                const titleElement = warehouseSubMenu.querySelector('.el-sub-menu__title')
                if (titleElement) {
                  // 移除其他子菜单的激活状态
                  subMenus.forEach(subMenu => {
                    const title = subMenu.querySelector('.el-sub-menu__title')
                    if (title) {
                      title.classList.remove('is-active')
                      // 移除内联样式
                      title.style.backgroundColor = ''
                      title.style.borderRight = ''
                      title.style.color = ''
                      title.style.fontWeight = ''
                    }
                  })
                  // 激活目标子菜单的 title
                  titleElement.classList.add('is-active')
                  // 使用内联样式确保样式生效（优先级最高）
                  titleElement.style.backgroundColor = '#ecf5ff'
                  titleElement.style.borderRight = '3px solid #409eff'
                  titleElement.style.color = '#409eff'
                  titleElement.style.fontWeight = '600'
                  const subIndex = warehouseSubMenu.getAttribute('index')
                  console.log('成功激活子菜单标题，index:', subIndex, 'target:', newActiveMenu, 'title:', titleElement.textContent?.trim())
                  
                  // 同时展开子菜单（即使 index 是 null，也尝试通过其他方式展开）
                  if (menuRef.value) {
                    try {
                      // 尝试通过 index 展开
                      if (subIndex) {
                        menuRef.value.open(subIndex)
                      } else {
                        // 如果 index 是 null，尝试通过点击 title 来展开
                        const clickEvent = new MouseEvent('click', { bubbles: true, cancelable: true })
                        titleElement.dispatchEvent(clickEvent)
                      }
                    } catch (e) {
                      console.warn('展开子菜单失败:', e)
                    }
                  }
                }
                // 注意：sub-menu 的 title 不是 menu-item，所以不需要继续查找 menu-item
                return
              }
            }
            
            // 方式3: 如果精确匹配失败，尝试匹配仓库管理的各种路径格式
            if (!targetItem && newActiveMenu.includes('warehouse')) {
              targetItem = Array.from(menuItems).find(item => {
                const itemIndex = item.getAttribute('index')
                return itemIndex === '/home/internal-warehouse' ||
                       itemIndex === '/home/assistant/warehouse' ||
                       itemIndex === '/home/internal/warehouse' ||
                       itemIndex?.includes('warehouse')
              })
            }
            
            // 方式4: 通过菜单文本内容匹配（作为最后的手段）
            if (!targetItem && newActiveMenu.includes('warehouse')) {
              targetItem = Array.from(menuItems).find(item => {
                const text = item.textContent?.trim() || ''
                return text.includes('仓库') || text.includes('warehouse')
              })
            }
            
            if (targetItem) {
              // 确保目标项有激活状态
              targetItem.classList.add('is-active', 'router-link-active', 'router-link-exact-active')
              console.log('成功激活菜单项，index:', targetItem.getAttribute('index'), 'target:', newActiveMenu)
            } else {
              console.warn('未找到匹配的菜单项 DOM 元素，target:', newActiveMenu)
              console.warn('所有菜单项 index:', allIndexes)
            }
            
            // 调用 syncMenuActive 作为备用
            syncMenuActive()
          }, 200)
        } catch (error) {
          console.warn('设置菜单激活状态失败:', error)
        }
      }
    })
  },
  { immediate: true }
)

const getUserTypeName = (userType) => {
  const typeMap = {
    'SALESMAN': '业务员',
    'MANAGER': '管理员',
    'ACCOUNTANT': '会计',
    'ASSISTANT': '内勤',
    'SYSTEM_ADMIN': '系统管理员'
  }
  return typeMap[userType] || userType
}

const getUserTypeTagType = (userType) => {
  const typeMap = {
    'SALESMAN': 'info',
    'MANAGER': 'success',
    'ACCOUNTANT': 'warning',
    'ASSISTANT': '',
    'SYSTEM_ADMIN': 'danger'
  }
  return typeMap[userType] || ''
}

const handleLogout = async () => {
  try {
    await logout()
  } catch (error) {
    console.error('退出失败:', error)
  } finally {
    // 无论退出成功与否，都清除本地存储并跳转
    localStorage.removeItem('accessToken')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('userInfo')
    ElMessage.success('退出成功')
    // 使用 replace 避免历史记录问题，并确保跳转到登录页
    router.replace('/login')
  }
}
</script>

<style scoped>
.home-container {
  width: 100%;
  height: 100vh;
}

.header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 30px;
  height: 60px;
  line-height: 60px;
}

.header-left h2 {
  margin: 0;
  font-size: 24px;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 20px;
}

.change-password-tip {
  font-size: 14px;
  color: #666;
  margin-bottom: 16px;
}

.change-password-form :deep(.el-input__wrapper) {
  width: 260px;
}

.change-password-notice {
  margin-top: 12px;
  font-size: 12px;
  color: #999;
  line-height: 1.6;
}

.message-icon-wrapper {
  position: relative;
  cursor: pointer;
  padding: 10px;
  border-radius: 50%;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
  margin-top: 8px; /* 图标向下移动一点 */
}

.message-icon-wrapper:hover {
  background-color: rgba(255, 255, 255, 0.2);
  transform: scale(1.1);
}

.message-icon-wrapper .message-icon {
  color: #fff;
  transition: all 0.3s ease;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
}

.message-badge {
  position: relative;
}

.message-badge :deep(.el-badge__content) {
  position: absolute;
  top: 50%;
  right: -8px;
  transform: translateY(-50%);
  border: 2px solid #fff;
  font-weight: 600;
  font-size: 12px;
  min-width: 20px;
  height: 20px;
  line-height: 16px;
  padding: 0 6px;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(245, 108, 108, 0.4);
}

/* 只有在有未读消息时才激活摇晃动效 */
.message-icon-wrapper.has-unread {
  animation: shake 1s ease-in-out infinite;
}

/* 摇晃动效 - 更醒目的效果 */
@keyframes shake {
  0%, 100% {
    transform: translateX(0) rotate(0deg);
  }
  10%, 30%, 50%, 70%, 90% {
    transform: translateX(-4px) rotate(-5deg);
  }
  20%, 40%, 60%, 80% {
    transform: translateX(4px) rotate(5deg);
  }
}

/* 没有未读消息时，不应用任何动效 */
.message-icon-wrapper:not(.has-unread) {
  animation: none;
}


.user-info {
  font-size: 14px;
}

.main-layout {
  height: calc(100vh - 60px);
  overflow: hidden;
}

.sidebar {
  background-color: #fff;
  border-right: 1px solid #e6e6e6;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  transition: width 0.3s;
}

/* 侧边栏收起/展开按钮容器 - 定位在右侧中间 */
.sidebar-toggle-wrapper {
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 侧边栏收起/展开按钮 */
.sidebar-toggle {
  width: 20px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background-color: #f5f7fa;
  border-radius: 4px 0 0 4px;
  border: 1px solid #e4e7ed;
  border-right: none;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  color: #606266;
  position: relative;
  overflow: hidden;
  box-shadow: -2px 0 4px rgba(0, 0, 0, 0.05);
}

.sidebar-toggle::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background-color: #409eff;
  transform: translate(-50%, -50%);
  transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1), height 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  opacity: 0.1;
}

.sidebar-toggle:hover {
  background-color: #ecf5ff;
  color: #409eff;
  border-color: #409eff;
  transform: translateX(-2px);
  box-shadow: -2px 0 8px rgba(64, 158, 255, 0.3);
}

.sidebar-toggle:hover::before {
  width: 100%;
  height: 100%;
}

.sidebar-toggle:active {
  transform: translateX(-2px) scale(0.95);
}

/* 收起状态下的按钮样式 */
.sidebar.el-aside[style*="64px"] .sidebar-toggle-wrapper {
  right: 0;
}

.sidebar.el-aside[style*="64px"] .sidebar-toggle {
  width: 18px;
  height: 36px;
}

.sidebar.el-aside[style*="64px"] .sidebar-toggle:hover {
  transform: translateX(2px);
}

/* 图标旋转动画 */
.sidebar-toggle .rotate-icon {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.sidebar-menu {
  border-right: none;
  height: 100%;
}

/* 收起状态下的菜单样式优化 */
.sidebar-menu.el-menu--collapse {
  width: 64px;
}

.sidebar-menu.el-menu--collapse .el-menu-item,
.sidebar-menu.el-menu--collapse .el-sub-menu__title {
  padding-left: 20px !important;
  padding-right: 20px !important;
  text-align: center;
}

/* 收起状态下隐藏文字，只显示图标 */
.sidebar-menu.el-menu--collapse .el-menu-item span,
.sidebar-menu.el-menu--collapse .el-sub-menu__title span {
  height: 0;
  width: 0;
  overflow: hidden;
  visibility: hidden;
  display: inline-block;
}

/* 收起状态下隐藏角标 */
.sidebar-menu.el-menu--collapse .menu-badge-inline {
  display: none;
}

/* 收起状态下，确保菜单项图标居中 */
.sidebar-menu.el-menu--collapse .el-menu-item .el-icon,
.sidebar-menu.el-menu--collapse .el-sub-menu__title .el-icon {
  margin-right: 0;
}

/* 收起状态下，菜单项宽度固定 */
.sidebar-menu.el-menu--collapse .el-menu-item,
.sidebar-menu.el-menu--collapse .el-sub-menu__title {
  width: 64px;
  min-width: 64px;
}

/* 收起状态下，确保菜单文本容器不占用空间 */
.sidebar-menu.el-menu--collapse .menu-text-wrapper {
  display: none;
}

.sidebar-menu .el-menu-item,
.sidebar-menu .el-sub-menu__title {
  height: 50px;
  line-height: 50px;
  padding-left: 20px !important;
  padding-right: 20px !important;
  transition: all 0.3s;
  position: relative;
  overflow: visible;
}

/* 确保子菜单标题激活时，背景色和边框能正确显示 */
.sidebar-menu .el-sub-menu__title.is-active {
  background-color: #ecf5ff !important;
  border-right: 3px solid #409eff !important;
}

/* 二级菜单增加缩进 */
.sidebar-menu .el-sub-menu .el-menu-item,
.sidebar-menu .el-sub-menu .el-sub-menu__title {
  padding-left: 40px !important;
}

/* 三级菜单再多一级缩进（如果有的话） */
.sidebar-menu .el-sub-menu .el-sub-menu .el-menu-item,
.sidebar-menu .el-sub-menu .el-sub-menu .el-sub-menu__title {
  padding-left: 60px !important;
}

.sidebar-menu .el-menu-item:hover,
.sidebar-menu .el-sub-menu__title:hover {
  background-color: #f5f7fa;
  color: #409eff;
}

.sidebar-menu .el-menu-item.is-active {
  color: #409eff !important;
  background-color: #ecf5ff !important;
  font-weight: 600;
  border-right: 3px solid #409eff;
}

.sidebar-menu .el-sub-menu.is-active > .el-sub-menu__title {
  color: #409eff !important;
}

/* 子菜单标题激活状态样式（通过 JS 直接添加 is-active 类到 title 元素）
   参考普通菜单项的激活样式，保持一致 - 包括蓝色背景和右侧蓝色竖线 */
.sidebar-menu .el-sub-menu__title.is-active {
  color: #409eff !important;
  background-color: #ecf5ff !important;
  font-weight: 600 !important;
  border-right: 3px solid #409eff !important;
}

/* 提高优先级，确保样式能覆盖 Element Plus 的默认样式 */
.sidebar-menu .el-sub-menu .el-sub-menu__title.is-active {
  color: #409eff !important;
  background-color: #ecf5ff !important;
  font-weight: 600 !important;
  border-right: 3px solid #409eff !important;
}

.sidebar-menu .el-sub-menu__title.is-active .el-icon {
  color: #409eff !important;
}

.sidebar-menu .el-sub-menu__title.is-active span {
  color: #409eff !important;
}

.sidebar-menu .el-sub-menu .el-menu-item.is-active {
  color: #409eff !important;
  background-color: #ecf5ff !important;
  font-weight: 600;
  border-right: 3px solid #409eff;
}

/* 菜单角标样式 - 角标显示在菜单名称的正右边，并在菜单项中垂直居中 */
.sidebar-menu .menu-item-with-badge {
  position: relative;
}

/* 菜单项容器 */
.sidebar-menu .menu-item-with-badge :deep(.el-menu-item__content) {
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  width: 100%;
}

/* 角标容器 */
.sidebar-menu .menu-badge-inline {
  position: relative;
  margin-left: auto; /* 推到最右边 */
  margin-right: 8px; /* 与右边距 */
}

/* 角标内容 */
.sidebar-menu .menu-badge-inline :deep(.el-badge__content) {
  position: absolute;
  right: -8px;
  top: -8px;
  transform: scale(0.85);
  z-index: 10;
}

.sidebar-menu .menu-text-wrapper {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

/* 可点击的子菜单标题样式 */
.sidebar-menu .sub-menu-title-clickable {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  cursor: pointer;
  flex: 1;
}

.sidebar-menu .sub-menu-title-clickable:hover {
  color: #409eff;
}

.sidebar-menu .menu-badge-inline {
  display: inline-block;
  margin-left: 0;
  vertical-align: middle;
}

.sidebar-menu .menu-badge-inline :deep(.el-badge__content) {
  font-size: 12px;
  height: 18px;
  line-height: 18px;
  min-width: 18px;
  padding: 0 5px;
  border-radius: 9px;
}

/* 确保选中状态的菜单项有正确的样式 */
.sidebar-menu .el-menu-item[aria-current="page"],
.sidebar-menu .el-menu-item.is-active[aria-current="page"] {
  color: #409eff !important;
  background-color: #ecf5ff !important;
  font-weight: 600;
  border-right: 3px solid #409eff;
}

/* 确保选中状态的菜单项内的链接也有正确的颜色 */
.sidebar-menu .el-menu-item.is-active a {
  color: #409eff !important;
}

/* 强制应用选中样式，使用 router-link-active 类（Element Plus 在使用 :router="true" 时会自动添加） */
.sidebar-menu .el-menu-item.router-link-active,
.sidebar-menu .el-menu-item.router-link-exact-active {
  color: #409eff !important;
  background-color: #ecf5ff !important;
  font-weight: 600;
  border-right: 3px solid #409eff;
}

.sidebar-menu :deep(.router-link-active),
.sidebar-menu :deep(.router-link-exact-active) {
  color: #409eff !important;
}

/* 确保链接占满菜单项，选中样式可见 */
.sidebar-menu :deep(.el-menu-item a) {
  display: block;
  width: 100%;
  height: 100%;
}

/* 二级菜单项右移 */
.sidebar-menu .el-sub-menu .el-menu-item {
  padding-left: 40px !important;
}

.content-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.tabs-container {
  background-color: #fff;
  border-bottom: 1px solid #e6e6e6;
  height: 40px;
  overflow-x: auto;
  overflow-y: hidden;
  flex-shrink: 0;
  width: 100%;
  position: relative;
  z-index: 10;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.tabs-container::-webkit-scrollbar {
  height: 4px;
}

.tabs-container::-webkit-scrollbar-thumb {
  background-color: #c0c4cc;
  border-radius: 2px;
}

.tabs-container::-webkit-scrollbar-thumb:hover {
  background-color: #a0a4ac;
}

.tabs-wrapper {
  display: flex;
  align-items: center;
  height: 100%;
  padding: 0 10px;
  white-space: nowrap;
  min-width: 100%;
}

.tab-item {
  display: inline-flex;
  align-items: center;
  padding: 0 15px;
  height: 32px;
  margin-right: 5px;
  background-color: #f5f5f5;
  border: 1px solid #e6e6e6;
  border-radius: 4px 4px 0 0;
  cursor: pointer;
  transition: all 0.3s;
  user-select: none;
}

.tab-item:hover {
  background-color: #e8e8e8;
}

.tab-item.active {
  background-color: #fff;
  border-bottom-color: #fff;
  color: #409eff;
  font-weight: 500;
}

.tab-item.fixed {
  background-color: #409eff;
  color: #fff;
  border-color: #409eff;
}

.tab-item.fixed.active {
  background-color: #409eff;
  color: #fff;
}

.tab-title {
  margin-right: 8px;
  font-size: 13px;
}

.tab-close {
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
  padding: 2px;
  border-radius: 2px;
}

.tab-close:hover {
  background-color: rgba(0, 0, 0, 0.1);
}

.tab-item.fixed .tab-close {
  display: none;
}

/* 标签栏右键菜单样式 */
.tab-context-menu {
  position: fixed;
  background-color: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  z-index: 9999;
  min-width: 140px;
  padding: 4px 0;
}

.context-menu-item {
  display: flex;
  align-items: center;
  padding: 8px 16px;
  cursor: pointer;
  font-size: 14px;
  color: #606266;
  transition: background-color 0.2s;
}

.context-menu-item:hover {
  background-color: #f5f7fa;
  color: #409eff;
}

.context-menu-item .el-icon {
  margin-right: 8px;
  font-size: 16px;
}

.main-content {
  background-color: #f0f2f5;
  padding: 20px;
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  height: calc(100vh - 100px);
}

/* 首页仪表盘样式 */
.home-dashboard {
  padding: 20px;
  min-height: calc(100vh - 100px);
}

/* 欢迎横幅 */
.welcome-banner {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  padding: 30px 40px;
  margin-bottom: 24px;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  color: #fff;
}

/* 管理员横幅 - 更商务、高端的风格 */
.welcome-banner.manager-banner {
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 50%, #7e8ba3 100%);
  box-shadow: 0 6px 20px rgba(30, 60, 114, 0.4);
  position: relative;
  overflow: hidden;
}

.welcome-banner.manager-banner::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
  animation: pulse 15s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 0.5;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
}

.banner-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.banner-left {
  flex: 1;
}

.welcome-title {
  font-size: 32px;
  font-weight: 600;
  margin: 0 0 10px 0;
  display: flex;
  align-items: center;
  gap: 10px;
}

.greeting {
  font-weight: 400;
  opacity: 0.9;
}

.username {
  font-weight: 700;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.welcome-subtitle {
  font-size: 16px;
  opacity: 0.9;
  margin: 0;
}

.banner-right {
  display: flex;
  align-items: center;
}

.user-avatar {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  border: 3px solid rgba(255, 255, 255, 0.3);
}

/* 统计卡片网格 */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
  margin-bottom: 24px;
}

/* 管理员统计卡片网格 - 更精致的样式 */
.manager-stats-grid {
  gap: 24px;
}

.stat-card {
  border-radius: 12px;
  transition: all 0.3s ease;
  border: none;
  overflow: hidden;
  cursor: pointer;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.stat-card-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
}

.stat-card-success {
  background: linear-gradient(135deg, #56ab2f 0%, #a8e063 100%);
  color: #fff;
}

.stat-card-warning {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  color: #fff;
}

.stat-card-info {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  color: #fff;
}

/* 管理员统计卡片 - 更商务、高端的配色 */
.manager-stat-card-primary {
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.1);
  cursor: pointer;
}

.manager-stat-card-warning {
  background: linear-gradient(135deg, #c94b4b 0%, #f17373 100%);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.1);
  cursor: pointer;
}

.manager-stat-card-success {
  background: linear-gradient(135deg, #134e5e 0%, #71b280 100%);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.1);
  cursor: pointer;
}

.manager-stat-card-info {
  background: linear-gradient(135deg, #2c3e50 0%, #4a6741 100%);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.1);
  cursor: pointer;
}

.manager-stat-card-primary:hover,
.manager-stat-card-warning:hover,
.manager-stat-card-success:hover,
.manager-stat-card-info:hover {
  transform: translateY(-6px);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.2);
}

/* 管理员统计卡片图标 */
.manager-icon-primary {
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
}

.manager-icon-warning {
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
}

.manager-icon-success {
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
}

/* 业务员统计卡片网格 */
.salesman-stats-grid {
  gap: 20px;
}

.salesman-stat-card-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  cursor: pointer;
}

.salesman-stat-card-success {
  background: linear-gradient(135deg, #56ab2f 0%, #a8e063 100%);
  color: #fff;
  cursor: pointer;
}

.salesman-stat-card-warning {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  color: #fff;
  cursor: pointer;
}

.salesman-stat-card-info {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  color: #fff;
  cursor: pointer;
}

.salesman-stat-card-primary:hover,
.salesman-stat-card-success:hover,
.salesman-stat-card-warning:hover,
.salesman-stat-card-info:hover {
  transform: translateY(-6px);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.2);
}

.salesman-icon-primary,
.salesman-icon-success,
.salesman-icon-warning,
.salesman-icon-info {
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
}

/* 快捷操作网格 */
.quick-actions-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.quick-action-card {
  border-radius: 12px;
  transition: all 0.3s ease;
  cursor: pointer;
  border: 1px solid #e4e7ed;
}

.quick-action-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  border-color: #409eff;
}

.quick-action-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  text-align: center;
}

.quick-action-icon {
  color: #409eff;
  margin-bottom: 12px;
}

.quick-action-label {
  font-size: 14px;
  color: #606266;
  font-weight: 500;
}

.manager-icon-info {
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
}

.stat-content {
  display: flex;
  align-items: center;
  padding: 10px;
}

.stat-icon {
  width: 70px;
  height: 70px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 20px;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
}

.stat-icon.primary {
  background: rgba(255, 255, 255, 0.25);
}

.stat-icon.success {
  background: rgba(255, 255, 255, 0.25);
}

.stat-icon.warning {
  background: rgba(255, 255, 255, 0.25);
}

.stat-icon.info {
  background: rgba(255, 255, 255, 0.25);
}

.stat-info {
  flex: 1;
}

.stat-label {
  font-size: 14px;
  opacity: 0.9;
  margin-bottom: 8px;
}

.stat-value {
  font-size: 28px;
  font-weight: 700;
  line-height: 1;
}

/* 主要内容网格 */
.main-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.left-column,
.right-column {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.info-card {
  border-radius: 12px;
  transition: all 0.3s ease;
}

.info-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.info-card :deep(.el-card__header) {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  border-bottom: 1px solid #e4e7ed;
  padding: 15px 20px;
}

.info-card .card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: #303133;
}

.info-card .card-header .el-icon {
  font-size: 18px;
  color: #409eff;
}

/* 用户信息列表 */
.user-info-list {
  padding: 10px 0;
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
}

.info-item:last-child {
  border-bottom: none;
}

.info-label {
  color: #909399;
  font-size: 14px;
}

.info-value {
  color: #303133;
  font-weight: 500;
  font-size: 14px;
}

/* 快捷操作 */
.quick-actions {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  padding: 10px 0;
}

.action-btn {
  width: 100%;
  height: 45px;
  font-size: 14px;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.action-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.3);
}

/* 提示内容 */
.tips-content {
  padding: 10px 0;
}

.tip-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
  color: #606266;
  font-size: 14px;
}

.tip-item:last-child {
  border-bottom: none;
}

.tip-icon {
  color: #67c23a;
  font-size: 18px;
  flex-shrink: 0;
}

/* 帮助内容 */
.help-content {
  padding: 10px 0;
}

.help-item {
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
  color: #606266;
  font-size: 14px;
  line-height: 1.6;
}

.help-item:last-child {
  border-bottom: none;
}

.help-item strong {
  color: #303133;
  font-weight: 600;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .main-grid {
    grid-template-columns: 1fr;
  }
  
}

@media (max-width: 768px) {
  .welcome-banner {
    padding: 20px;
  }
  
  .welcome-title {
    font-size: 24px;
    flex-direction: column;
    align-items: flex-start;
  }
  
  .stats-grid {
    grid-template-columns: 1fr;
  }
  
  .quick-actions {
    grid-template-columns: 1fr;
  }
  
  .user-avatar {
    width: 60px;
    height: 60px;
  }
}

/* 未读消息提醒样式 */
:deep(.unread-message-alert) {
  min-width: 400px;
  font-size: 14px;
  padding: 15px 20px;
}

:deep(.unread-message-alert .el-message__content) {
  line-height: 1.6;
}
</style>

